import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { PlannerExperience, PlannerSection, PlannerSummary } from '../types/planner';
import {
  TripParameters,
  ActivityPhoto,
  ChatSummary,
  TripAttachment,
  ChatMessage,
} from '../types/chat';
import { WeatherReport } from '../types/weather';
import { generateTravelPlan, sendFollowUpMessage, getActivityPhotos, getDestinationInfo } from '../services/travelApi';
import { useWeatherReport } from '../hooks/useApiQueries';
import { persistentCache } from '../utils/persistentCache';
import { createLogger } from '../utils/logger';
import { saveRecentTripSnapshot } from '../utils/recentTrips';
import {
  createChat as apiCreateChat,
  getUserChats,
  getChatById,
  getChatMessages,
  createMessage as apiCreateMessage,
  updateChat as apiUpdateChat,
  attachTripToChat,
  setActiveTrip as apiSetActiveTrip,
  generateSessionId,
} from '../services/chatApi';
import { restoreSearch } from '../services/searchHistoryApi';

interface TravelActivity {
  id: string;
  name: string;
  description?: string;
  category?: string[];
  address?: string;
  duration?: string;
  cost?: string;
  time_of_visit?: string;
  working_hours?: string;
  notes?: string;
  location_name?: string;
  main_attraction?: string;
  reason?: string;
  is_preliminary?: boolean;
  from_database?: boolean;
  activity_id?: string;
  day_number?: number;
}

interface OverflowDayActivities {
  day_number: number;
  activities: TravelActivity[];
}

interface SpecialActivity {
  special_id: string;
  title: string;
  snippet: string;
  url: string;
  event_date?: string;
  categories: string[];
  image_url?: string;
  city: string;
  country: string;
  source?: string;
  score?: number;
}

interface CityPlan {
  city: string;
  country: string;
  city_key: string;
  reason?: string;
  transport_notes?: string;
  day_start: number;
  day_end: number;
  destination_info_id?: string;
  image_urls?: string[];
  weather?: any;
  tips?: string[];
  safety_notes?: string[];
  packing_list?: string[];
  additional_info?: string[];
}

interface CountrySummary {
  country: string;
  description?: string;
  image_urls?: string[];
}

interface TravelData {
  destination: string;
  start_date?: string;
  end_date?: string;
  travel_plan?: Array<{
    day_number?: number;
    date?: string;
    notes?: string;
    city?: string;
    city_key?: string;
    is_skeleton?: boolean;
    activities: Array<{
      id: string;
      name: string;
      time_of_visit?: string;
      is_preliminary?: boolean;
      category?: string[];
      from_database?: boolean;
      activity_id?: string;
    }>;
  }>;
  activities?: TravelActivity[];
  overflow_activities?: OverflowDayActivities[];
  deleted_activities?: Array<TravelActivity | Record<string, any>>;
  total_trip_days?: number;
  restaurants?: Array<Record<string, any>>;
  additional_info?: string[];
  safety_notes?: string[];
  packing_list?: string[];
  budget?: string;
  preferred_activity_types?: string[];
  is_preliminary?: boolean;
  weather?: WeatherReport | null;
  plan_mode?: 'single_city' | 'multi_city';
  cities?: CityPlan[];
  country_summary?: CountrySummary;
  country_destination_info?: DestinationInfo;
  initial_query?: string;
  user_additional_info?: string | null;
}

interface DestinationInfo {
  name: string;
  city: string;
  country: string;
  description: string;
  history: string;
  culture: string;
  attractions: string[];
  interesting_facts: string[];
  bestTimeToVisit: string;
  currency: string;
  language: string;
  timezone: string;
  climate: string;
  normalized_destination?: string;
  image_url?: string;
  image_urls?: string[];
  safety_notes?: string[];
  packing_list?: string[];
}

export interface McpToolMetadata {
  category: string;
  tool_name: string;
  preview: string;
  full_output: string;
}

type TripSaveResult = {
  status?: string;
  search_id?: string | null;
};

type ConfirmationSource = 'initial' | 'followup';

interface ConfirmationPrompt {
  id: string;
  prompt: string;
  source: ConfirmationSource;
  retryMessage?: string;
  tripParameters?: TripParameters;
  responded?: 'yes' | 'no';
}

export interface PlannerMessage {
  id: string;
  text: string;
  isUser: boolean;
  phase?: string | null;
  isPreliminary?: boolean;
  timestamp: string;
  mcpMetadata?: McpToolMetadata[];
  confirmation?: ConfirmationPrompt;
  tripCard?: {
    searchId: string;
    destination: string;
    startDate?: string | null;
    endDate?: string | null;
    days?: number | null;
    activities?: number | null;
    photoUrl?: string | null;
  };
}

type HydrateOptions = {
  merge?: boolean;
  searchId?: string | null;
  destinationInfo?: DestinationInfo | null;
  countryDestinationInfo?: DestinationInfo | null;
};

type PlanRequestOptions = {
  confirmCreation?: boolean;
  skipUserMessage?: boolean;
};

type FollowUpOptions = {
  confirmCreation?: boolean;
  skipUserMessage?: boolean;
};

interface PlannerContextValue {
  travelData: TravelData | null;
  isMultiCityMode: boolean;
  destinationInfo: DestinationInfo | null;
  countryDestinationInfo: DestinationInfo | null;
  isDestinationInfoLoading: boolean;
  destinationInfoError: string | null;
  setDestinationInfo: (info: DestinationInfo | null) => void;
  setCountryDestinationInfo: (info: DestinationInfo | null) => void;
  summary: PlannerSummary | null;
  sections: PlannerSection[];
  restaurants: PlannerExperience[];
  specialActivities: Record<string, SpecialActivity[]>;
  weather: WeatherReport | null;
  isWeatherLoading: boolean;
  tips: string[];
  safetyNotes: string[];
  packingList: string[];
  activeExperience: PlannerExperience | null;
  messages: PlannerMessage[];
  loading: boolean;
  loadingPhase: string | null;
  isGeneratingTripPlan: boolean;
  isAnyOperationInProgress: boolean;
  setOperationInProgress: (inProgress: boolean) => void;
  error: string | null;
  currentSearchId: string | null;
  destinationImageUrls: string[];
  updateDestinationImages: () => Promise<void>;
  chats: ChatSummary[];
  chatsLoading: boolean;
  currentChatId: string | null;
  attachedTrips: TripAttachment[];
  activeChatTripId: string | null;
  refreshChats: () => Promise<void>;
  selectChat: (chatId: string) => Promise<void>;
  startNewChat: () => Promise<string | null>;
  markTripAsActive: (searchId: string) => Promise<void>;
  applySaveResult: (saveResult?: TripSaveResult | null) => Promise<string | null>;
  setShareViewLock: (searchId: string | null) => void;
  hydrateFromTravelData: (data: Partial<TravelData>, options?: HydrateOptions) => void;
  toggleExperienceSelection: (id: string) => void;
  toggleAll: (checked: boolean) => void;
  openExperience: (experience: PlannerExperience | null) => void;
  generatePlan: (parameters: TripParameters, options?: PlanRequestOptions) => Promise<void>;
  sendFollowUp: (message: string, options?: FollowUpOptions) => Promise<void>;
  respondToConfirmation: (confirmationId: string, choice: 'yes' | 'no') => Promise<void>;
  resetConversation: () => void;
  resetPlanner: () => void;
  clearCurrentPlan: () => void;
}

const PlannerContext = React.createContext<PlannerContextValue | undefined>(undefined);

const formatDateRangeSummary = (start?: string, end?: string, dayCount?: number): string | undefined => {
  if (!start && !end && !dayCount) return undefined;

  const startDate = start ? new Date(start) : undefined;
  const endDate = end ? new Date(end) : undefined;

  if (startDate && endDate) {
    const formatter = new Intl.DateTimeFormat('en', { month: 'long', day: 'numeric' });
    const startLabel = formatter.format(startDate);
    const endLabel = formatter.format(endDate);
    return `${startLabel} – ${endLabel}${dayCount ? ` • ${dayCount} day${dayCount > 1 ? 's' : ''}` : ''}`;
  }

  if (dayCount) {
    return `${dayCount} day${dayCount > 1 ? 's' : ''}`;
  }

  return undefined;
};

const normalizeImageUrl = (src: string): string => {
  const trimmed = (src || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('data:')) {
    return trimmed;
  }

  try {
    let candidate = trimmed;
    if (candidate.startsWith('//')) {
      candidate = `https:${candidate}`;
    }

    if (/^https?:/i.test(candidate)) {
      const parsed = new URL(candidate);
      return `${parsed.protocol}//${parsed.host}${parsed.pathname}`.toLowerCase();
    }
  } catch (error) {
    // Ignore parse errors and fall back to manual string handling
  }

  const withoutQuery = trimmed.split('?')[0];
  return withoutQuery.toLowerCase();
};

const sanitizePhotoUrls = (photos: ActivityPhoto[] | undefined, limit = 6): string[] => {
  if (!photos || photos.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const cleaned: string[] = [];

  for (const photo of photos) {
    const url = (photo?.url || '').trim();
    if (!url) continue;

    const lower = url.toLowerCase();
    if (lower.includes('placeholder')) continue;
    if (lower.includes('via.placeholder.com')) continue;

    const normalizedKey = normalizeImageUrl(url);
    if (!normalizedKey) continue;
    if (seen.has(normalizedKey)) continue;
    seen.add(normalizedKey);
    cleaned.push(url);
    if (cleaned.length >= limit) {
      break;
    }
  }

  return cleaned;
};

const extractStaticImageUrls = (item: Record<string, unknown> | undefined, limit = 6): string[] => {
  if (!item || typeof item !== 'object') {
    return [];
  }

  const urls: string[] = [];
  const seen = new Set<string>();

  const consider = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    const key = normalizeImageUrl(trimmed);
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    urls.push(trimmed);
  };

  if (Array.isArray((item as any).imageUrls)) {
    (item as any).imageUrls.forEach(consider);
  }

  if (typeof (item as any).imageUrl === 'string') {
    consider((item as any).imageUrl);
  }

  if (typeof (item as any).image_url === 'string') {
    consider((item as any).image_url);
  }

  if (Array.isArray((item as any).images)) {
    (item as any).images.forEach((img: unknown) => {
      if (typeof img === 'string') {
        consider(img);
      } else if (img && typeof img === 'object' && typeof (img as any).url === 'string') {
        consider((img as any).url);
      }
    });
  }

  if (urls.length > limit) {
    return urls.slice(0, limit);
  }

  return urls;
};

const PLAN_VERB_TRAVEL_NOUN_REGEX =
  /\b(generate|create|plan|make|design|build|craft)\b[\s\S]*?\b(trip|itinerary|vacation|holiday|getaway|escape|journey|tour|adventure|experience)\b/i;
const PLAN_WITH_DESTINATION_REGEX =
  /\b(generate|create|plan|make|design|build|craft)\b[\s\S]*?\b(?:to|for|in)\b[\s\S]*?[A-Z][a-zA-Z]+/i;
const TRAVEL_VERB_DESTINATION_REGEX =
  /\b(travel|visit|go|head|fly)\b[\s\S]*?\bto\b[\s\S]*?[A-Z][a-zA-Z]+/i;
const REQUEST_ANOTHER_PLAN_REGEX =
  /\b(?:new|another|brand\s+new)\b[\s\S]*?\b(trip|plan|itinerary|vacation|getaway|escape|tour|adventure)\b/i;
const PLANNING_VERB_REGEX = /\b(generate|create|plan|make|design|build|craft)\b/i;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _isNewTripIntent = (message: string, hasExistingPlan: boolean): boolean => {
  const normalized = message.trim();
  if (!normalized) {
    return false;
  }

  if (PLAN_VERB_TRAVEL_NOUN_REGEX.test(normalized)) {
    return true;
  }

  if (PLAN_WITH_DESTINATION_REGEX.test(normalized)) {
    return true;
  }

  if (TRAVEL_VERB_DESTINATION_REGEX.test(normalized)) {
    return true;
  }

  if (REQUEST_ANOTHER_PLAN_REGEX.test(normalized)) {
    return true;
  }

  if (!hasExistingPlan && PLANNING_VERB_REGEX.test(normalized)) {
    return true;
  }

  return false;
};

const buildPlannerSection = (travelData: TravelData, activityPhotos: Record<string, string[]> = {}): PlannerSection[] => {
  const activitiesById = new Map<string, TravelActivity>();
  (travelData.activities || []).forEach((activity) => {
    activitiesById.set(activity.id, activity);
  });

  // Build set of deleted activity IDs for fast lookup
  const deletedActivityIds = new Set<string>();
  (travelData.deleted_activities || []).forEach((deletedActivity: any) => {
    if (deletedActivity?.id) {
      deletedActivityIds.add(deletedActivity.id);
    }
  });

  const sections: PlannerSection[] = [];
  const isMultiCity = travelData.plan_mode === 'multi_city' || (travelData.cities && travelData.cities.length > 1);
  const citiesMap = new Map<string, CityPlan>();
  if (isMultiCity && travelData.cities) {
    travelData.cities.forEach((city) => {
      citiesMap.set(city.city_key, city);
    });
  }

  (travelData.travel_plan || []).forEach((day, index) => {
    const dayNumber = typeof day.day_number === 'number' ? day.day_number : index + 1;
    const formatter = day.date && !Number.isNaN(Date.parse(day.date))
      ? new Intl.DateTimeFormat('en', { month: 'long', day: 'numeric', weekday: 'short' })
      : null;

    // Filter out deleted activities from the day's activities
    const activeActivities = day.activities.filter(activityRef => !deletedActivityIds.has(activityRef.id));

    const experiences: PlannerExperience[] = activeActivities.map((activityRef, order) => {
      const details = activitiesById.get(activityRef.id);
      const fallback = details ?? (activityRef as unknown as TravelActivity);
      const preliminaryFlag = Boolean((activityRef as any)?.is_preliminary || fallback?.is_preliminary);
      const category = fallback?.category || (Array.isArray((activityRef as any)?.category) ? (activityRef as any)?.category : undefined);

      const extraDetails = fallback ? ({ ...fallback, day_number: dayNumber } as Record<string, unknown>) : undefined;

      return {
        id: activityRef.id,
        name: activityRef.name || fallback?.name || `Activity ${order + 1}`,
        description: fallback?.description,
        category,
        timeOfVisit: fallback?.time_of_visit || activityRef.time_of_visit,
        address: fallback?.address,
        duration: fallback?.duration,
        cost: fallback?.cost,
        workingHours: fallback?.working_hours,
        notes: fallback?.notes,
        badge: fallback?.main_attraction,
        extraDetails,
        isSelected: true, // All activities are selected by default
        isPreliminary: preliminaryFlag,
        imageUrl: activityPhotos[activityRef.id]?.[0],
        imageUrls: activityPhotos[activityRef.id],
      };
    });

    // Build title with date and city for multi-city trips
    let title = `Day ${dayNumber}`;
    if (formatter) {
      const dateStr = formatter.format(new Date(day.date!));
      if (isMultiCity && (day.city || day.city_key)) {
        const cityKey = day.city_key || day.city;
        const cityInfo = cityKey ? citiesMap.get(cityKey) : null;
        const cityName = cityInfo?.city || day.city;
        if (cityName) {
          title = `Day ${dayNumber} • ${dateStr} • ${cityName}`;
        } else {
          title = `Day ${dayNumber} • ${dateStr}`;
        }
      } else {
        title = `Day ${dayNumber} • ${dateStr}`;
      }
    } else if (isMultiCity && (day.city || day.city_key)) {
      const cityKey = day.city_key || day.city;
      const cityInfo = cityKey ? citiesMap.get(cityKey) : null;
      const cityName = cityInfo?.city || day.city;
      if (cityName) {
        title = `Day ${dayNumber} • ${cityName}`;
      }
    }
    const subtitle = day.notes || `${experiences.length} curated experiences`;

    sections.push({
      id: `day-${dayNumber}`,
      title,
      subtitle,
      experiences,
      dayNumber,
    });
  });

  const overflowGroups = travelData.overflow_activities || [];
  if (overflowGroups.length > 0) {
    const baseDayCount = travelData.travel_plan?.length ?? 0;
    const totalDays = travelData.total_trip_days ?? baseDayCount + overflowGroups.length;
    const overflowTitle = totalDays > baseDayCount + 1
      ? `Days ${baseDayCount + 1} – ${totalDays}`
      : `Day ${baseDayCount + 1}`;

    const overflowExperiences: PlannerExperience[] = [];
    overflowGroups.forEach((group) => {
      group.activities.forEach((activity, order) => {
        const details = activitiesById.get(activity.id) || activity;
        const badge = group.day_number ? `Day ${group.day_number}` : undefined;

        overflowExperiences.push({
          id: activity.id,
          name: activity.name || details?.name || `Activity ${order + 1}`,
          description: details?.description,
          category: details?.category,
          timeOfVisit: details?.time_of_visit,
          address: details?.address,
          duration: details?.duration,
          cost: details?.cost,
          workingHours: details?.working_hours,
          notes: details?.notes,
          badge: badge || details?.main_attraction,
          extraDetails: { ...details, day_number: group.day_number } as Record<string, unknown>,
          isSelected: true, // Pre-select overflow activities by default so they show on the map
          isPreliminary: Boolean(details?.is_preliminary),
          imageUrl: activityPhotos[activity.id]?.[0],
          imageUrls: activityPhotos[activity.id],
        });
      });
    });

    sections.push({
      id: 'day-overflow',
      title: overflowTitle,
      subtitle: `${overflowExperiences.length} flexible suggestions for the remaining days`,
      experiences: overflowExperiences,
      dayNumber: undefined,
    });
  }

  return sections;
};

const buildRestaurants = (travelData: TravelData, activityPhotos: Record<string, string[]> = {}): PlannerExperience[] => {
  // Build set of deleted activity IDs (including restaurants) for fast lookup
  const deletedActivityIds = new Set<string>();
  (travelData.deleted_activities || []).forEach((deletedActivity: any) => {
    if (deletedActivity?.id) {
      deletedActivityIds.add(deletedActivity.id);
    }
  });

  if (travelData.restaurants && travelData.restaurants.length > 0) {
    // Filter out deleted restaurants
    const activeRestaurants = travelData.restaurants.filter((restaurant: any) =>
      !deletedActivityIds.has(restaurant.id)
    );

    return activeRestaurants.map((restaurant: any) => {
      const result = {
        id: restaurant.id,
        name: restaurant.name,
        description: restaurant.description,
        category: restaurant.cuisine_type,
        address: restaurant.address,
        cost: restaurant.price_range,
        workingHours: restaurant.working_hours,
        notes: restaurant.tips,
        badge: restaurant.reason,
        highlights: restaurant.signature_dishes,
        isSelected: true,
        extraDetails: restaurant as Record<string, unknown>,
        isPreliminary: Boolean(restaurant.is_preliminary),
        imageUrl: activityPhotos[restaurant.id]?.[0],
        imageUrls: activityPhotos[restaurant.id],
        city: restaurant.city,
        city_key: restaurant.city_key,
      };
      return result;
    });
  }

  const restaurantKeywords = ['restaurant', 'dinner', 'lunch', 'bar', 'food', 'café', 'cafe'];
  const result: PlannerExperience[] = [];

  (travelData.activities || []).forEach((activity) => {
    const categories = (activity.category || []).map((c) => c.toLowerCase());
    if (categories.some((category) => restaurantKeywords.includes(category))) {
      result.push({
        id: activity.id,
        name: activity.name,
        description: activity.description,
        category: activity.category,
        address: activity.address,
        cost: activity.cost,
        workingHours: activity.working_hours,
        notes: activity.notes,
        isSelected: true,
        extraDetails: activity as unknown as Record<string, unknown>,
        isPreliminary: Boolean((activity as any).is_preliminary),
        imageUrl: activityPhotos[activity.id]?.[0],
        imageUrls: activityPhotos[activity.id],
      });
    }
  });

  return result;
};

const buildSummary = (travelData: TravelData): PlannerSummary | null => {
  if (!travelData.destination) return null;
  const dayCount = travelData.total_trip_days ?? travelData.travel_plan?.length;

  // For multi-city trips, use the overall trip dates, not individual city dates
  let endDate = travelData.end_date;
  if (travelData.plan_mode === 'multi_city') {
    // For multi-city trips, prefer the overall trip end_date from state
    // If not available, calculate from start_date and total days
    if (!endDate && travelData.start_date && dayCount) {
      const start = new Date(travelData.start_date);
      const end = new Date(start);
      end.setDate(end.getDate() + dayCount - 1);
      endDate = end.toISOString().split('T')[0];
    }
    // Don't use dates from individual travel_plan days as those are city-specific
  }

  // For multi-city trips, construct a better destination display
  let destinationDisplay = travelData.destination;
  if (travelData.plan_mode === 'multi_city' && travelData.cities && travelData.cities.length > 1) {
    // Show country name, or if destination is already country, use it
    // Otherwise, show city names in parentheses
    const cityNames = travelData.cities.map(c => c.city).filter(Boolean);
    if (cityNames.length > 0) {
      // Check if destination is already the country name
      const destinationLower = travelData.destination.toLowerCase();
      const firstCityCountry = travelData.cities[0]?.country?.toLowerCase();
      if (destinationLower === firstCityCountry || !travelData.destination.includes(',')) {
        // Destination is country name, use it
        destinationDisplay = travelData.destination;
      } else {
        // Destination is a city, use country name if available
        if (firstCityCountry && travelData.cities.every(c => c.country?.toLowerCase() === firstCityCountry)) {
          destinationDisplay = travelData.cities[0].country;
        } else {
          destinationDisplay = travelData.destination;
        }
      }
    }
  }

  return {
    destination: destinationDisplay,
    overview: undefined,
    profile: {
      month: travelData.start_date ? new Intl.DateTimeFormat('en', { month: 'long' }).format(new Date(travelData.start_date)) : undefined,
      durationLabel: formatDateRangeSummary(travelData.start_date, endDate, dayCount),
      party: undefined,
    },
  };
};

const createMessage = (partial: Omit<PlannerMessage, 'id' | 'timestamp'>): PlannerMessage => ({
  ...partial,
  id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  timestamp: new Date().toISOString(),
});

const normalizePreliminaryState = (payload: any): Partial<TravelData> | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const mapActivity = (activity: any, dayNumber?: number): TravelActivity => {
    const normalizedCategory = Array.isArray(activity?.category)
      ? activity.category
      : activity?.category
        ? [activity.category]
        : undefined;

    return {
      id: activity?.id,
      name: activity?.name,
      description: activity?.description,
      category: normalizedCategory,
      address: activity?.address,
      duration: activity?.duration,
      cost: activity?.cost,
      time_of_visit: activity?.time_of_visit,
      working_hours: activity?.working_hours,
      notes: activity?.notes,
      location_name: activity?.location_name,
      main_attraction: activity?.main_attraction,
      reason: activity?.reason,
      is_preliminary: activity?.is_preliminary,
      from_database: activity?.from_database,
      activity_id: activity?.activity_id,
      day_number: dayNumber,
    };
  };

  const preliminaryDays: Array<{ day_number: number; date?: string; activities: TravelActivity[] }> = Array.isArray(payload.travel_plan)
    ? payload.travel_plan.map((day: any, index: number) => {
        const dayNumber = typeof day?.day_number === 'number' ? day.day_number : index + 1;
        const activities = Array.isArray(day?.activities)
          ? day.activities.map((activity: any) => mapActivity(activity, dayNumber))
          : [];

        return {
          day_number: dayNumber,
          date: day?.date,
          activities,
        };
      })
    : [];

  const overflowDays: Array<{ day_number: number; activities: TravelActivity[] }> = Array.isArray(payload.overflow_days)
    ? (payload.overflow_days
        .map((day: any, index: number) => {
          const fallbackDayNumber = preliminaryDays.length + index + 1;
          const dayNumber = typeof day?.day_number === 'number' ? day.day_number : fallbackDayNumber;
          const activities = Array.isArray(day?.activities)
            ? day.activities.map((activity: any) => mapActivity(activity, dayNumber))
            : [];

          return {
            day_number: dayNumber,
            activities,
          };
        })
        .filter((entry: { day_number: number; activities: TravelActivity[] }): entry is { day_number: number; activities: TravelActivity[] } => entry.activities.length > 0))
    : [];

  const flattenedActivities: TravelActivity[] = [
    ...preliminaryDays.flatMap((day) => day.activities),
    ...overflowDays.flatMap((day) => day.activities),
  ];

  const travelPlan: TravelData['travel_plan'] = preliminaryDays.map((day) => ({
    day_number: day.day_number,
    date: day.date,
    activities: day.activities.map((activity) => ({
      id: activity.id,
      name: activity.name,
      time_of_visit: activity.time_of_visit,
      is_preliminary: activity.is_preliminary,
      category: activity.category,
      from_database: activity.from_database,
      activity_id: activity.activity_id,
    })),
  })) as TravelData['travel_plan'];

  const overflowActivities: OverflowDayActivities[] = overflowDays.map((day) => ({
    day_number: day.day_number ?? 0,
    activities: day.activities,
  }));

  return {
    destination: payload.destination,
    start_date: payload.start_date,
    end_date: payload.end_date,
    travel_plan: travelPlan as TravelData['travel_plan'],
    activities: flattenedActivities,
    overflow_activities: overflowActivities,
    total_trip_days: typeof payload.total_days === 'number' ? payload.total_days : undefined,
    restaurants: (payload.restaurants || []) as TravelData['restaurants'],
    is_preliminary: true,
  };
};

const PLANNER_STATE_KEY = 'planner-state';
const PLANNER_STATE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface PersistedPlannerState {
  travelData: TravelData | null;
  sections: PlannerSection[];
  restaurants: PlannerExperience[];
  specialActivities: Record<string, SpecialActivity[]>;
  summary: PlannerSummary | null;
  messages: PlannerMessage[];
  searchId: string | null;
  chatId: string | null;
}

export const PlannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded: isAuthLoaded } = useClerkAuth();
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [travelData, setTravelData] = React.useState<TravelData | null>(null);
  const [sections, setSections] = React.useState<PlannerSection[]>([]);
  const [restaurants, setRestaurants] = React.useState<PlannerExperience[]>([]);
  const [specialActivities, setSpecialActivities] = React.useState<Record<string, SpecialActivity[]>>({});
  const [summary, setSummary] = React.useState<PlannerSummary | null>(null);
  const [activeExperience, setActiveExperience] = React.useState<PlannerExperience | null>(null);
  const [messages, setMessages] = React.useState<PlannerMessage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingPhase, setLoadingPhase] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [currentSearchId, setCurrentSearchId] = React.useState<string | null>(null);
  const [chats, setChats] = React.useState<ChatSummary[]>([]);
  const [chatsLoading, setChatsLoading] = React.useState<boolean>(true);
  const [currentChatId, setCurrentChatId] = React.useState<string | null>(null);
  const [attachedTrips, setAttachedTrips] = React.useState<TripAttachment[]>([]);
  const [activeChatTripId, setActiveChatTripId] = React.useState<string | null>(null);
  const [activityPhotos, setActivityPhotos] = React.useState<Record<string, string[]>>({});
  const deliveredTripCardsRef = React.useRef<Set<string>>(new Set());
  const [destinationImageUrls, setDestinationImageUrls] = React.useState<string[]>([]);
  const destinationImagesPrevRef = React.useRef<string[] | null>(null);
  const [destinationInfo, setDestinationInfoRaw] = React.useState<DestinationInfo | null>(null);
  const [countryDestinationInfo, setCountryDestinationInfo] = React.useState<DestinationInfo | null>(null);
  const [destinationInfoError, setDestinationInfoError] = React.useState<string | null>(null);
  const [isDestinationInfoStreaming, setIsDestinationInfoStreaming] = React.useState(false);
  const [isDestinationInfoFetching, setIsDestinationInfoFetching] = React.useState(false);
  const [isMultiCityMode, setIsMultiCityMode] = React.useState(false);
  const [isGeneratingTripPlan, setIsGeneratingTripPlan] = React.useState(false);
  const [isAnyOperationInProgress, setIsAnyOperationInProgress] = React.useState(false);

  const isExternalHydrationRef = React.useRef(false);

  // Wrapped setter that also clears loading flags
  const setDestinationInfo = React.useCallback((info: DestinationInfo | null) => {
    setDestinationInfoRaw(info);
    // Clear loading flags when manually setting destination info
    // Also clear error state
    if (info) {
      // Setting valid destination info - clear all loading/error state
      setIsDestinationInfoFetching(false);
      setIsDestinationInfoStreaming(false);
      setDestinationInfoError(null);
    } else {
      // Setting to null - allow fetch to happen by clearing loading flags
      // This is important when navigating between trips
      setIsDestinationInfoFetching(false);
      setIsDestinationInfoStreaming(false);
    }
  }, []);

  // Debug: Track hero image array changes
  React.useEffect(() => {
    const prev = destinationImagesPrevRef.current;
    const prevLen = prev?.length ?? 0;
    const nextLen = destinationImageUrls.length;
    if (prevLen !== nextLen || JSON.stringify(prev) !== JSON.stringify(destinationImageUrls)) {
    }
    destinationImagesPrevRef.current = destinationImageUrls;
  }, [destinationImageUrls]);

  const previousDestinationRef = React.useRef<string | null>(null);
  const lastSnapshotSignatureRef = React.useRef<string | null>(null);
  const destinationInfoFetchRef = React.useRef<string | null>(null);
  const creatingChatRef = React.useRef(false);
  const pendingNavigationRef = React.useRef(false);
  const lastRestoredChatRef = React.useRef<string | null>(null);
  const lastRestoredTripRef = React.useRef<string | null>(null);
  const restoringActiveTripRef = React.useRef<string | null>(null);
  const sharedTripLockRef = React.useRef<string | null>(null);

  const mapChatMessageToPlanner = React.useCallback((message: ChatMessage): PlannerMessage => ({
    id: message.message_id,
    text: message.content,
    isUser: message.role === 'user',
    timestamp: message.timestamp,
    mcpMetadata: Array.isArray(message.metadata?.mcp_metadata)
      ? message.metadata?.mcp_metadata
      : undefined,
    tripCard: message.metadata?.trip_card ? {
      searchId: message.metadata.trip_card.searchId,
      destination: message.metadata.trip_card.destination,
      startDate: message.metadata.trip_card.startDate,
      endDate: message.metadata.trip_card.endDate,
      days: message.metadata.trip_card.days,
      activities: message.metadata.trip_card.activities,
      photoUrl: message.metadata.trip_card.photoUrl,
    } : undefined,
  }), []);

  const refreshChats = React.useCallback(async () => {
    if (!isSignedIn) {
      setChats([]);
      setChatsLoading(false);
      return;
    }
    setChatsLoading(true);
    try {
      const response = await getUserChats(50, 0, false);
      setChats(response.chats);
      if (response.chats.length > 0) {
        setCurrentChatId((prev) => prev ?? response.chats[0].chat_id);
      }
    } catch (error) {
      plannerLogger.error('Failed to load chats:', error);
    } finally {
      setChatsLoading(false);
    }
  }, [isSignedIn]);

  // Load chats when auth state is resolved and user is signed in
  React.useEffect(() => {
    if (isAuthLoaded && isSignedIn) {
      refreshChats();
    }
  }, [isAuthLoaded, isSignedIn, refreshChats]);

  // Clear chats when user signs out
  React.useEffect(() => {
    if (isAuthLoaded && !isSignedIn) {
      setChats([]);
      setChatsLoading(false);
    }
  }, [isAuthLoaded, isSignedIn]);

  const setShareViewLock = React.useCallback((searchId: string | null) => {
    sharedTripLockRef.current = searchId;
  }, []);

  const loadChatMessages = React.useCallback(async (chatId: string) => {
    try {
      const PAGE_SIZE = 200;
      let allMessages: ChatMessage[] = [];
      let skip = 0;
      let total = Infinity;

      // Fetch the entire history so older trips/cards remain visible in chat
      while (skip < total) {
        const response = await getChatMessages(chatId, PAGE_SIZE, skip);
        const batch = Array.isArray(response.messages) ? response.messages : [];

        allMessages = allMessages.concat(batch);
        total = Number.isFinite(response.total) ? response.total : total;
        skip += batch.length;

        if (batch.length < PAGE_SIZE) {
          break; // No more pages to fetch
        }
      }

      const sortedMessages = allMessages.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );

      setMessages(sortedMessages.map(mapChatMessageToPlanner));
    } catch (error) {
      plannerLogger.error('Failed to load chat messages:', error);
      setMessages([]);
    }
  }, [mapChatMessageToPlanner]);

  const loadChatDetails = React.useCallback(async (chatId: string) => {
    try {
      const response = await getChatById(chatId);
      setAttachedTrips(response.attached_trips || []);
      const activeId = response.chat?.active_trip_id ?? response.attached_trips?.[0]?.search_id ?? null;
      setActiveChatTripId(activeId);
    } catch (error) {
      plannerLogger.error('Failed to load chat details:', error);
      setAttachedTrips([]);
      setActiveChatTripId(null);
    }
  }, []);

  React.useEffect(() => {
    if (!currentChatId) {
      setMessages([]);
      setAttachedTrips([]);
      setActiveChatTripId(null);
      return;
    }

    loadChatDetails(currentChatId);
    loadChatMessages(currentChatId);
  }, [currentChatId, loadChatDetails, loadChatMessages]);

  // Ensure every chat points to an active trip if attachments exist
  React.useEffect(() => {
    if (!currentChatId || activeChatTripId || attachedTrips.length === 0) {
      return;
    }
    const firstTripId = attachedTrips[0]?.search_id;
    if (firstTripId) {
      setActiveChatTripId(firstTripId);
    }
  }, [currentChatId, activeChatTripId, attachedTrips]);

  React.useEffect(() => {
    const destinationName = travelData?.destination?.trim() || null;
    if (destinationName === previousDestinationRef.current) {
      return;
    }

    previousDestinationRef.current = destinationName;

    // Only clear the fetch ref if the destination info doesn't match the new destination
    // This prevents unnecessary re-fetches when restoring trips that already have destination info
    const destLower = destinationName?.toLowerCase() || '';

    // For multi-city trips, check countryDestinationInfo instead of destinationInfo
    const isMultiCity = travelData?.plan_mode === 'multi_city';
    const relevantInfo = isMultiCity ? countryDestinationInfo : destinationInfo;

    const infoMatches = relevantInfo && destLower && (
      relevantInfo.name?.toLowerCase() === destLower ||
      relevantInfo.city?.toLowerCase() === destLower ||
      relevantInfo.country?.toLowerCase() === destLower ||
      relevantInfo.normalized_destination?.toLowerCase().includes(destLower) ||
      // For multi-city, also match if destination contains the country name
      (isMultiCity && relevantInfo.country?.toLowerCase() && destLower.includes(relevantInfo.country.toLowerCase()))
    );

    if (!infoMatches) {
      destinationInfoFetchRef.current = null;
    }

  }, [travelData?.destination, travelData?.plan_mode, destinationInfo, countryDestinationInfo]);

  // Restore state on mount
  React.useEffect(() => {
    const restoreState = async () => {
      // Early check: if external hydration happened synchronously or very quickly
      if (isExternalHydrationRef.current) {
         return;
      }

      try {
        const cached = await persistentCache.get<PersistedPlannerState>(PLANNER_STATE_KEY);
        
        // Re-check after await: external hydration might have happened while we were waiting
        if (isExternalHydrationRef.current) {
          setIsHydrated(true);
          return;
        }

        if (cached) {
          // Check if we are on a shared trip page
          // Use a robust regex that captures the segment after /trip/ ignoring query params or trailing slashes
          const match = window.location.pathname.match(/\/trip\/([^/?]+)/);
          const urlSearchId = match ? match[1] : null;


          // Always restore chat state
          setMessages(cached.messages);
          setCurrentChatId(cached.chatId ?? null);

          // Trip restoration logic
          const shouldRestoreTrip = (() => {
            // If we are not on a trip page, always restore
            if (!urlSearchId) return true;
            
            // If we are on a trip page, only restore if the cached ID matches
            // If cached.searchId is missing but we have data, we assume it's NOT the right trip to be safe
            if (cached.searchId && urlSearchId === cached.searchId) return true;

            return false;
          })();

          if (shouldRestoreTrip) {
            setTravelData(cached.travelData);
            setSections(cached.sections);
            setRestaurants(cached.restaurants);
            setSpecialActivities(cached.specialActivities);
            setSummary(cached.summary);
            setCurrentSearchId(cached.searchId ?? null);
          } else {
          }
        }
      } catch (error) {
        plannerLogger.error('Failed to restore planner state:', error);
      } finally {
        setIsHydrated(true);
      }
    };
    restoreState();
  }, []);

  // Persist state whenever it changes (debounced)
  React.useEffect(() => {
    if (!isHydrated) return; // Don't persist during initial hydration

    const persistState = async () => {
      try {
        const state: PersistedPlannerState = {
          travelData,
          sections,
          restaurants,
          specialActivities,
          summary,
          messages,
          searchId: currentSearchId,
          chatId: currentChatId,
        };
        await persistentCache.set(PLANNER_STATE_KEY, state, PLANNER_STATE_TTL);
        try {
          localStorage.setItem('tripgen_planner_state', JSON.stringify(state));
        } catch (storageError) {
          plannerLogger.error('Failed to persist planner state to localStorage:', storageError);
        }
      } catch (error) {
        plannerLogger.error('Failed to persist planner state:', error);
      }
    };

    const timeoutId = setTimeout(persistState, 500); // Debounce 500ms
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, travelData, sections, restaurants, specialActivities, summary, messages, currentSearchId]);

  // Fetch photos for activities when travelData changes
  React.useEffect(() => {
    if (!travelData) {
      return;
    }

    const fetchPhotosForActivities = async () => {
      const destinationName = travelData.destination || '';

      const initialPhotoMap: Record<string, string[]> = {};
      const photoTargets: Array<{ id: string; searchQuery: string; label: string }> = [];

      const registerStaticPhotos = (id: string, source: unknown): boolean => {
        if (!id) return false;
        if (initialPhotoMap[id]) return true;

        const staticUrls = extractStaticImageUrls(source as Record<string, unknown> | undefined);
        if (staticUrls.length > 0) {
          initialPhotoMap[id] = staticUrls;
          return true;
        }
        return false;
      };

      (travelData.activities || []).forEach((activity) => {
        if (!activity?.id) return;

        const existingState = activityPhotos[activity.id];
        if (existingState && existingState.length > 0) {
          initialPhotoMap[activity.id] = existingState;
          return;
        }

        if (registerStaticPhotos(activity.id, activity)) {
          return;
        }

        const searchQuery = (activity.main_attraction || activity.name || '').trim();
        if (!searchQuery) return;

        photoTargets.push({
          id: activity.id,
          searchQuery,
          label: activity.name || activity.id,
        });
      });

      (travelData.restaurants || []).forEach((restaurant: any) => {
        if (!restaurant?.id) return;

        const existingState = activityPhotos[restaurant.id];
        if (existingState && existingState.length > 0) {
          initialPhotoMap[restaurant.id] = existingState;
          return;
        }

        if (registerStaticPhotos(restaurant.id, restaurant)) {
          return;
        }

        const cuisine = Array.isArray(restaurant.cuisine_type)
          ? restaurant.cuisine_type.find((value: unknown): value is string => typeof value === 'string')
          : typeof restaurant.cuisine_type === 'string'
            ? restaurant.cuisine_type
            : undefined;

        const searchParts = [restaurant.name, cuisine, destinationName || restaurant.city, 'restaurant']
          .map(part => (typeof part === 'string' ? part.trim() : ''))
          .filter(Boolean);

        const searchQuery = searchParts.join(' ');
        if (!searchQuery) return;

        photoTargets.push({
          id: restaurant.id,
          searchQuery,
          label: restaurant.name || restaurant.id,
        });
      });

      if (photoTargets.length === 0) {
        if (Object.keys(initialPhotoMap).length > 0) {
          setActivityPhotos(prev => ({ ...prev, ...initialPhotoMap }));
        }
        return;
      }

      const photoPromises = photoTargets.map(async (target) => {
        try {
          const photos = await getActivityPhotos(target.searchQuery, destinationName, target.id);

          const validUrls = sanitizePhotoUrls(photos);
          if (validUrls.length > 0) {
            return { activityId: target.id, photoUrls: validUrls };
          }
        } catch (error) {
          plannerLogger.error(' [Planner] Error fetching photo for', target.id, error);
        }
        return null;
      });

      const results = await Promise.all(photoPromises);

      const resultMap: Record<string, string[] | undefined> = {};
      results.forEach(result => {
        if (result && result.photoUrls.length > 0) {
          resultMap[result.activityId] = result.photoUrls;
        }
      });
      setActivityPhotos(prev => {
        const next: Record<string, string[]> = { ...initialPhotoMap };

        photoTargets.forEach(target => {
          const fromFetch = resultMap[target.id];
          if (fromFetch && fromFetch.length > 0) {
            next[target.id] = fromFetch;
            return;
          }

          if (!next[target.id] && prev[target.id]?.length) {
            next[target.id] = prev[target.id];
          }
        });

        Object.entries(prev).forEach(([key, value]) => {
          if (!next[key] && value?.length) {
            next[key] = value;
          }
        });

        return next;
      });
    };

    fetchPhotosForActivities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelData]);

  // Rebuild sections and restaurants when photos are loaded
  React.useEffect(() => {
    if (travelData && Object.keys(activityPhotos).length > 0) {
      setSections(buildPlannerSection(travelData, activityPhotos));
      setRestaurants(buildRestaurants(travelData, activityPhotos));
    }
  }, [activityPhotos, travelData]);

  // Use React Query hooks for weather
  const { data: weatherData, isLoading: isWeatherLoading } = useWeatherReport(travelData?.destination);

  // Destination info can stream in real-time or be fetched on-demand for restored trips
  const isDestinationInfoLoading = isDestinationInfoStreaming || isDestinationInfoFetching;

  // Ensure weather is always null if undefined
  const weather = weatherData ?? null;

  // Keep isMultiCityMode in sync with current travel data
  React.useEffect(() => {
    const shouldBeMulti =
      travelData?.plan_mode === 'multi_city' ||
      Boolean((travelData as any)?.multi_city_plan);
    setIsMultiCityMode(shouldBeMulti);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelData?.plan_mode, (travelData as any)?.multi_city_plan]);

  // Helper function to check if destinationInfo matches current destination
  const doesDestinationInfoMatchDestination = React.useCallback(
    (info: DestinationInfo | null, destinationName?: string | null) => {
      if (!info || !destinationName) {
        return false;
      }

      const normalizedDestinationName = destinationName.trim().toLowerCase();
      if (!normalizedDestinationName) {
        return false;
      }

      const normalizedInfoDestination = info.normalized_destination?.trim().toLowerCase();
      if (normalizedInfoDestination && normalizedInfoDestination === normalizedDestinationName) {
        return true;
      }

      const infoCity = info.city?.trim().toLowerCase();
      if (infoCity && normalizedDestinationName.startsWith(infoCity)) {
        return true;
      }

      const infoName = info.name?.trim().toLowerCase();
      if (infoName && normalizedDestinationName.startsWith(infoName)) {
        return true;
      }

      // Check if destination exactly equals the name (for regions like Catalonia)
      if (infoName && infoName === normalizedDestinationName) {
        return true;
      }

      // Check if the normalized_destination contains the destination name
      // This handles cases like destination="Catalonia" and normalized="Barcelona, Catalonia, Spain"
      if (normalizedInfoDestination && normalizedInfoDestination.includes(normalizedDestinationName)) {
        return true;
      }

      // Check if destination is the country name
      const infoCountry = info.country?.trim().toLowerCase();
      if (infoCountry && infoCountry === normalizedDestinationName) {
        return true;
      }

      // Check if destination contains the country (for multi-city trips)
      if (infoCountry && normalizedDestinationName.includes(infoCountry)) {
        return true;
      }

      return false;
    },
    [],
  );

  // Set destination images immediately when destination info loads
  // ONLY for single-city trips - multi-city trips use countryDestinationInfo
  React.useEffect(() => {
    if (!destinationInfo) {
      return;
    }

    // Skip if this is a multi-city trip - images should come from countryDestinationInfo
    const isMultiCity = travelData?.plan_mode === 'multi_city';
    if (isMultiCity) {
      return;
    }

    // Always set images from destinationInfo when it's available
    // This ensures images load on first page load and overwrites any fallback images
    if (destinationInfo.image_urls && destinationInfo.image_urls.length > 0) {
      setDestinationImageUrls(destinationInfo.image_urls.slice(0, 1));
    } else if (destinationInfo.image_url) {
      // Fallback to legacy single image
      setDestinationImageUrls([destinationInfo.image_url]);
    } else {
      // Destination changed but has no images - clear any stale images so fallback fetch can run
      setDestinationImageUrls([]);
    }
  }, [destinationInfo, travelData?.plan_mode, travelData?.destination, doesDestinationInfoMatchDestination]);

  // Debug: Track destinationInfo state changes
  React.useEffect(() => {
    if (destinationInfo) {
    } else {
    }
  }, [destinationInfo]);

  const lastDestinationForImagesRef = React.useRef<string | null>(null);
  const lastDestinationCityRef = React.useRef<string | null>(null);

  const normalizeDestinationForImages = React.useCallback((value?: string | null) => {
    if (!value) return { raw: null as string | null, city: null as string | null };
    const raw = value.trim().toLowerCase();
    if (!raw) return { raw: null as string | null, city: null as string | null };
    const city = raw.split(',')[0]?.trim() || raw;
    return { raw, city };
  }, []);

  // Clear stale hero images whenever the canonical destination string changes
  React.useEffect(() => {
    const destinationName = travelData?.destination?.trim() || null;
    const lastDestination = lastDestinationForImagesRef.current;

    // Normalize to avoid clearing for cosmetic changes like adding/removing country
    const currentNorm = normalizeDestinationForImages(destinationName);
    const lastNorm = normalizeDestinationForImages(lastDestination);

    const hasMeaningfulChange =
      currentNorm.raw &&
      lastNorm.raw &&
      currentNorm.raw !== lastNorm.raw &&
      currentNorm.city !== lastNorm.city;

    if (hasMeaningfulChange) {
      setDestinationImageUrls([]);
    }

    lastDestinationForImagesRef.current = destinationName;
    lastDestinationCityRef.current = currentNorm.city;
  }, [travelData?.destination, normalizeDestinationForImages]);

  // Debug: Track countryDestinationInfo state changes
  React.useEffect(() => {
    if (countryDestinationInfo) {
    } else {
    }
  }, [countryDestinationInfo]);

  // Set destination images from countryDestinationInfo for multi-city trips
  React.useEffect(() => {
    const isMultiCity = travelData?.plan_mode === 'multi_city';
    if (!isMultiCity || !countryDestinationInfo) {
      return;
    }

    // Always set images from countryDestinationInfo for multi-city trips
    // This ensures images load on first page load and overwrites any fallback images
    if (countryDestinationInfo.image_urls && countryDestinationInfo.image_urls.length > 0) {
      setDestinationImageUrls(countryDestinationInfo.image_urls.slice(0, 1));
    } else if (countryDestinationInfo.image_url) {
      // Fallback to legacy single image
      setDestinationImageUrls([countryDestinationInfo.image_url]);
    } else {
      // Country changed but has no images - clear stale city images so fallback fetch can run
      setDestinationImageUrls([]);
    }
  }, [countryDestinationInfo, travelData?.plan_mode, travelData?.destination]);

  // Fetch and store destination image if not in destinationInfo or countryDestinationInfo
  const travelDataCities = (travelData as any)?.cities;
  React.useEffect(() => {
    const destinationName = travelData?.destination?.trim();
    if (!destinationName) {
      return;
    }

    // Skip if we already have images
    if (destinationImageUrls.length > 0) {
      return;
    }

    const isMultiCity = travelData?.plan_mode === 'multi_city';

    // For multi-city trips, check countryDestinationInfo; for single-city, check destinationInfo
    const relevantInfo = isMultiCity ? countryDestinationInfo : destinationInfo;

    // Skip if relevant info has images (already handled by previous effect)
    if (relevantInfo?.image_urls?.length || relevantInfo?.image_url) {
      return;
    }

    // Skip if relevant info doesn't match current destination
    if (relevantInfo && !doesDestinationInfoMatchDestination(relevantInfo, destinationName)) {
      return;
    }

    // Only fetch if we have relevant info but it lacks images
    if (!relevantInfo) {
      return;
    }

    // Fetch image and store in database
    const fetchAndStoreImage = async () => {
      try {
        // For multi-city trips, use the country name for destination-hero images
        // This ensures images are stored in the country-level destination document
        let searchQuery: string;
        let destinationForStorage: string;
        
        if (isMultiCity && countryDestinationInfo?.country) {
          // Multi-city trip: use country name for both search and storage
          searchQuery = countryDestinationInfo.country;
          destinationForStorage = countryDestinationInfo.country;
        } else if (destinationInfo?.city && destinationInfo?.country) {
          // Single city trip: use "City, Country" format
          searchQuery = `${destinationInfo.city} ${destinationInfo.country}`;
          destinationForStorage = `${destinationInfo.city}, ${destinationInfo.country}`;
        } else {
          // Fallback to destination name
          searchQuery = destinationName;
          destinationForStorage = destinationName;
        }

        plannerLogger.info(
          `Fetching destination-hero image for ${isMultiCity ? 'multi-city' : 'single-city'} trip`,
          { searchQuery, destinationForStorage, country: relevantInfo?.country }
        );

        // Pass the destination name for storage (country for multi-city, city for single-city)
        const photos = await getActivityPhotos(searchQuery, destinationForStorage, 'destination-hero');
        const photoUrls = sanitizePhotoUrls(photos, 1);

        if (photoUrls.length > 0) {
          setDestinationImageUrls([photoUrls[0]]);
        }
      } catch (error) {
        plannerLogger.error('Failed to fetch destination image:', error);
      }
    };

    fetchAndStoreImage();
  }, [destinationInfo, countryDestinationInfo, destinationImageUrls.length, travelData?.destination, travelData?.plan_mode, doesDestinationInfoMatchDestination]);

  const isViewingTrip = React.useMemo(() => {
    if (!location || !location.pathname) {
      return false;
    }
    return location.pathname.startsWith('/trip') || location.pathname.startsWith('/plan/');
  }, [location]);

  React.useEffect(() => {
    const destinationName = travelData?.destination?.trim();
    if (!destinationName || !isViewingTrip) {
      return;
    }

    // CRITICAL: For multi-city trips, NEVER fetch city-level destination info
    // Multi-city trips should ONLY use countryDestinationInfo (set during restore/hydration)
    // Only check indicators from CURRENT travelData, not stale state
    const isMultiCity = travelData?.plan_mode === 'multi_city' ||
                        Boolean((travelData as any)?.cities?.length > 0);

    if (isMultiCity) {
      // Skip all fetching for multi-city - country info comes from backend
      return;
    }

    // For single-city trips, check if we already have matching destination info
    if (doesDestinationInfoMatchDestination(destinationInfo, destinationName)) {
      return;
    }

    if (isDestinationInfoStreaming || isDestinationInfoFetching) {
      return;
    }

    if (destinationInfoFetchRef.current === destinationName) {
      return;
    }

    let isCancelled = false;
    destinationInfoFetchRef.current = destinationName;
    setIsDestinationInfoFetching(true);
    setDestinationInfoError(null);

    getDestinationInfo(destinationName)
      .then((info) => {
        if (isCancelled) {
          return;
        }
        setDestinationInfoRaw(info);
      })
      .catch((error: any) => {
        if (isCancelled) {
          return;
        }
        const message = error?.message || 'Failed to load destination information';
        setDestinationInfoError(message);
      })
      .finally(() => {
        // Always clear loading state, even if cancelled
        // This prevents the loading state from getting stuck
        setIsDestinationInfoFetching(false);
      });

    return () => {
      isCancelled = true;
      // Clear loading state on cleanup to prevent stuck loading indicators
      setIsDestinationInfoFetching(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    travelData?.destination,
    travelData?.plan_mode,
    travelDataCities,
    destinationInfo,
    countryDestinationInfo,
    doesDestinationInfoMatchDestination,
    isDestinationInfoStreaming,
    isDestinationInfoFetching,
    isViewingTrip,
    currentSearchId,
  ]);

  const updateDestinationImages = React.useCallback(async () => {
    // Destination images now come only from destinationInfo
    // No external fetching needed
  }, []);

  const latestUserMessage = React.useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.isUser) {
        return message.text;
      }
    }
    return undefined;
  }, [messages]);

  React.useEffect(() => {
    if (!travelData?.destination) {
      return;
    }

    const travelPlanLength = Array.isArray(travelData.travel_plan)
      ? travelData.travel_plan.length
      : undefined;

    const totalDays =
      travelData.total_trip_days ??
      travelPlanLength;

    const signature = [
      currentSearchId || 'local',
      travelData.destination,
      travelData.start_date || '',
      travelData.end_date || '',
      totalDays || travelPlanLength || 0,
    ].join('|');

    if (lastSnapshotSignatureRef.current === signature) {
      return;
    }

    lastSnapshotSignatureRef.current = signature;

    saveRecentTripSnapshot({
      id: currentSearchId || signature,
      destination: travelData.destination,
      startDate: travelData.start_date,
      endDate: travelData.end_date,
      totalDays,
      summary: formatDateRangeSummary(travelData.start_date, travelData.end_date, totalDays),
      searchId: currentSearchId,
      lastPrompt: latestUserMessage,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    travelData?.destination,
    travelData?.start_date,
    travelData?.end_date,
    travelData?.total_trip_days,
    travelData?.travel_plan?.length,
    currentSearchId,
    latestUserMessage,
  ]);

  const appendMessage = React.useCallback((message: Omit<PlannerMessage, 'id' | 'timestamp'>) => {
    setMessages((prev) => [...prev, createMessage(message)]);
  }, []);

  // Keep track of any trip cards that were already recorded (prevents duplicates on hydrate)
  React.useEffect(() => {
    messages.forEach((msg) => {
      if (msg.tripCard?.searchId) {
        deliveredTripCardsRef.current.add(msg.tripCard.searchId);
      }
    });
  }, [messages]);

  // Helper function to merge individual city data into multi-city structure
  const mergeCityDataIntoMultiCity = React.useCallback((prev: TravelData | null, cityData: Partial<TravelData>, cityKey?: string, city?: string): TravelData => {
    if (!prev || prev.plan_mode !== 'multi_city') {
      // Not multi-city, just merge normally
      return { ...(prev || {} as TravelData), ...cityData } as TravelData;
    }

    // Merge activities (deduplicate by ID)
    const existingActivities = new Map<string, TravelActivity>();
    (prev.activities || []).forEach((activity) => {
      existingActivities.set(activity.id, activity);
    });
    (cityData.activities || []).forEach((activity) => {
      existingActivities.set(activity.id, activity);
    });

    // Merge travel plan - preserve skeleton structure (day_number, date, city) while adding activities
    const existingTravelPlan = prev.travel_plan || [];
    const newTravelPlan = cityData.travel_plan || [];

    // Create a map of existing days by composite key (day_number + city_key) for accurate matching
    // Also maintain a fallback map by day_number only for backwards compatibility
    const existingDaysMap = new Map<string, typeof existingTravelPlan[0]>();
    const existingDaysByNumberMap = new Map<number, typeof existingTravelPlan[0]>();
    existingTravelPlan.forEach((day, index) => {
      const fallbackDayNumber = index + 1;
      const dayNum = typeof day.day_number === 'number' && day.day_number > 0
        ? day.day_number
        : fallbackDayNumber;
      const dayCityKey = day.city_key || day.city || '';
      const compositeKey = `${dayNum}-${dayCityKey}`;
      
      const dayWithNumber = {
        ...day,
        day_number: day.day_number ?? dayNum,
      };
      
      existingDaysMap.set(compositeKey, dayWithNumber);
      // Only add to number map if not already present (first occurrence wins)
      if (!existingDaysByNumberMap.has(dayNum)) {
        existingDaysByNumberMap.set(dayNum, dayWithNumber);
      }
    });

    // For multi-city, find which days in skeleton belong to this city
    // This handles the case where backend sends local day numbers (1, 2) instead of global (3, 4)
    const incomingCityKey = cityKey || (newTravelPlan[0]?.city_key as string) || (newTravelPlan[0]?.city as string);
    const cityInfo = prev.cities?.find(
      (c) => c.city_key === incomingCityKey || c.city === incomingCityKey
    );

    // Merge new days into existing skeleton structure
    newTravelPlan.forEach((newDay, localIndex) => {
      let targetDayNum = newDay.day_number || (localIndex + 1);
      const newDayCityKey = newDay.city_key || newDay.city || incomingCityKey || '';

      // If backend sent local day numbers (1, 2), convert to global using city's day_start
      if (cityInfo && targetDayNum <= cityInfo.day_end - cityInfo.day_start + 1) {
        // Check if this looks like a local day number (within city's day count)
        const cityDayCount = cityInfo.day_end - cityInfo.day_start + 1;
        if (targetDayNum <= cityDayCount && targetDayNum < cityInfo.day_start) {
          // This is a local day number, convert to global
          targetDayNum = cityInfo.day_start + (targetDayNum - 1);
        }
      }

      // First try to match by composite key (day_number + city_key) for accurate matching
      const compositeKey = `${targetDayNum}-${newDayCityKey}`;
      let existingDay = existingDaysMap.get(compositeKey);

      // If no match by composite key, try matching by day_number only (but verify city_key matches)
      if (!existingDay) {
        const dayByNumber = existingDaysByNumberMap.get(targetDayNum);
        if (dayByNumber) {
          const existingCityKey = dayByNumber.city_key || dayByNumber.city || '';
          // Only use this match if city_key matches or if we don't have city info yet
          // Relaxed: if newDay has NO city info, assume it matches the existing day at this number to preserve context
          if (existingCityKey === newDayCityKey || !newDayCityKey) {
            existingDay = dayByNumber;
          }
        }
      }

      if (existingDay) {
        // Preserve skeleton structure (day_number, date, city) but update with new activities
        // Don't replace date/city from skeleton - they're already correct
        const updatedDay = {
          ...existingDay,
          activities: newDay.activities || existingDay.activities,
          notes: newDay.notes && !newDay.notes.includes('Planning activities') ? newDay.notes : existingDay.notes,
          is_skeleton: false, // No longer a skeleton once we have real data
          day_number: existingDay.day_number ?? targetDayNum,
        };
        existingDaysMap.set(compositeKey, updatedDay);
        existingDaysByNumberMap.set(targetDayNum, updatedDay);
      } else {
        // New day not in skeleton - add it with city metadata and correct day number
        const newDayEntry = {
          ...newDay,
          day_number: targetDayNum,
          city: newDay.city || city,
          city_key: newDay.city_key || cityKey || incomingCityKey,
        };
        existingDaysMap.set(compositeKey, newDayEntry);
        // Only add to number map if not already present
        if (!existingDaysByNumberMap.has(targetDayNum)) {
          existingDaysByNumberMap.set(targetDayNum, newDayEntry);
        }
      }
    });

    // Build final merged travel plan - ensure each day_number appears only once
    // CRITICAL: We must preserve ALL days from the existing travel plan, even if they're not in the incoming data
    // This ensures that when city A's data arrives, we don't lose city B's skeleton days
    
    // First, ensure all existing days are in the map (in case they weren't updated)
    existingTravelPlan.forEach((day) => {
      const dayNum = typeof day.day_number === 'number' && day.day_number > 0
        ? day.day_number
        : existingTravelPlan.indexOf(day) + 1;
      const dayCityKey = day.city_key || day.city || '';
      const compositeKey = `${dayNum}-${dayCityKey}`;
      
      // Only add if not already present (don't overwrite updates)
      if (!existingDaysMap.has(compositeKey)) {
        existingDaysMap.set(compositeKey, {
          ...day,
          day_number: day.day_number ?? dayNum,
        });
      }
    });

    // Now build the final plan - each day_number should appear only once
    // In proper multi-city trips, each day_number is unique globally
    const daysByNumber = new Map<number, typeof existingTravelPlan[0]>();
    existingDaysMap.forEach((day) => {
      const dayNum = day.day_number || 0;
      if (dayNum === 0) return; // Skip invalid days
      
      const existing = daysByNumber.get(dayNum);
      if (!existing) {
        daysByNumber.set(dayNum, day);
      } else {
        // If we have a duplicate day_number (shouldn't happen, but handle it), prefer:
        // 1. Non-skeleton days over skeleton days
        // 2. Days matching the incoming city_key
        const isExistingSkeleton = existing.is_skeleton === true;
        const isNewSkeleton = day.is_skeleton === true;
        const existingCityKey = existing.city_key || existing.city || '';
        const newCityKey = day.city_key || day.city || '';
        
        if (isExistingSkeleton && !isNewSkeleton) {
          // Replace skeleton with real data
          daysByNumber.set(dayNum, day);
        } else if (!isExistingSkeleton && isNewSkeleton) {
          // Keep existing real data
          // daysByNumber already has the correct day
        } else if (incomingCityKey && newCityKey === incomingCityKey && existingCityKey !== incomingCityKey) {
          // Prefer the day that matches the incoming city
          daysByNumber.set(dayNum, day);
        }
        // Otherwise keep the existing one (first occurrence)
      }
    });

    // Sort days by day_number
    const mergedTravelPlan = Array.from(daysByNumber.values()).sort((a, b) => {
      const aDay = a.day_number || 0;
      const bDay = b.day_number || 0;
      return aDay - bDay;
    });

    // Merge restaurants (preserve existing, add new with city metadata)
    const existingRestaurants = prev.restaurants || [];
    const newRestaurants = cityData.restaurants || [];
    const restaurantsMap = new Map<string, typeof existingRestaurants[0]>();

    existingRestaurants.forEach((restaurant: any) => {
      restaurantsMap.set(restaurant.id, restaurant);
    });

    newRestaurants.forEach((restaurant: any) => {
      // Preserve existing restaurant city/city_key if already set
      // Only use the merge city/cityKey if restaurant doesn't have city info
      const existingRestaurant = restaurantsMap.get(restaurant.id);
      const restaurantCity = restaurant.city || existingRestaurant?.city || city;
      const restaurantCityKey = restaurant.city_key || existingRestaurant?.city_key || cityKey;
      
      restaurantsMap.set(restaurant.id, {
        ...restaurant,
        city: restaurantCity,
        city_key: restaurantCityKey,
      });
    });

    // Protect global fields from being overwritten by city-specific updates
    // Use prev values if they exist to maintain global trip state
    const totalDays = (prev.total_trip_days && cityData.total_trip_days && prev.total_trip_days > cityData.total_trip_days)
      ? prev.total_trip_days
      : (prev.total_trip_days ?? cityData.total_trip_days);

    // Merge deleted_activities arrays if both exist, preserving all deletions
    const mergedDeletedActivities = (() => {
      const prevDeleted = prev.deleted_activities || [];
      const newDeleted = cityData.deleted_activities || [];
      if (prevDeleted.length === 0 && newDeleted.length === 0) {
        return undefined;
      }
      // Merge and deduplicate by ID
      const deletedMap = new Map();
      [...prevDeleted, ...newDeleted].forEach(item => {
        if (item?.id) {
          deletedMap.set(item.id, item);
        }
      });
      return Array.from(deletedMap.values());
    })();

    return {
      ...prev,
      ...cityData,
      // Restore global fields
      destination: prev.destination ?? cityData.destination,
      start_date: prev.start_date ?? cityData.start_date,
      end_date: prev.end_date ?? cityData.end_date,
      total_trip_days: totalDays,

      activities: Array.from(existingActivities.values()),
      travel_plan: mergedTravelPlan,
      restaurants: Array.from(restaurantsMap.values()),
      deleted_activities: mergedDeletedActivities,
      // Preserve skeleton cities structure - use cityData.cities if prev.cities is empty
      cities: (prev.cities && prev.cities.length > 0) ? prev.cities : cityData.cities,
    } as TravelData;
  }, []);

  const hydrateFromTravelData = React.useCallback((data: Partial<TravelData>, options?: HydrateOptions) => {
    // Mark that external hydration has occurred, which will block restoreState from overwriting
    isExternalHydrationRef.current = true;

    if (!data || typeof data !== 'object') {
      return;
    }

    const shouldMerge = options?.merge ?? true;
    const hasSearchIdOption = options ? Object.prototype.hasOwnProperty.call(options, 'searchId') : false;
    const hasDestinationInfoOption = options ? Object.prototype.hasOwnProperty.call(options, 'destinationInfo') : false;
    const hasCountryDestinationInfoOption = options ? Object.prototype.hasOwnProperty.call(options, 'countryDestinationInfo') : false;
    let incomingPlanMode = (data as any)?.plan_mode;
    const incomingHasMultiCityPlan = Boolean((data as any)?.multi_city_plan);
    const citiesCount = Array.isArray((data as any)?.cities) ? (data as any)?.cities.length : 0;
    const countryInfoInData = (data as TravelData).country_destination_info;
    const countryInfoFromOptions = options?.countryDestinationInfo;
    let incomingIsMultiCity = incomingPlanMode === 'multi_city' ||
                              incomingHasMultiCityPlan ||
                              citiesCount > 1 ||
                              Boolean(countryInfoInData) ||
                              Boolean(countryInfoFromOptions);

    // 🔍 [HYDRATE] Debug: Log what we're receiving

    // Set destination info FIRST, before searchId
    // This prevents the auto-fetch useEffect from triggering when we restore a trip
    // CRITICAL: For multi-city trips, ONLY set countryDestinationInfo, NEVER destinationInfo
    if (hasDestinationInfoOption) {
      const destInfo = options?.destinationInfo ?? null;
      // Only set city-level destinationInfo if NOT a multi-city trip
      if (!incomingIsMultiCity) {
        setDestinationInfo(destInfo);
        // Preload hero image from restored destination info
        if (destInfo) {
          const heroUrls = destInfo.image_urls?.length ? destInfo.image_urls : (destInfo.image_url ? [destInfo.image_url] : []);
          if (heroUrls.length > 0) {
            setDestinationImageUrls(heroUrls.slice(0, 1));
          }
        }
        // Clear any stale country info for single-city trips
        setCountryDestinationInfo(null);
      } else {
        // Clear any existing city-level info for multi-city trips
        setDestinationInfo(null);
      }
    }
    if (hasCountryDestinationInfoOption) {
      const countryInfo = options?.countryDestinationInfo ?? null;
      setCountryDestinationInfo(countryInfo);
      // Preload hero image from restored country destination info
      if (countryInfo) {
        const heroUrls = countryInfo.image_urls?.length ? countryInfo.image_urls : (countryInfo.image_url ? [countryInfo.image_url] : []);
        if (heroUrls.length > 0) {
          setDestinationImageUrls(heroUrls.slice(0, 1));
        }
      }
      // Clear city-level info when setting country info
      if (countryInfo) {
        setDestinationInfo(null);
      }
      if (countryInfo || incomingIsMultiCity) {
        setIsMultiCityMode(true);
        incomingIsMultiCity = true;
        if (!incomingPlanMode && countryInfo) {
          incomingPlanMode = 'multi_city';
          (data as any).plan_mode = 'multi_city';
        }
      }
    }

    // Set country destination info if present in the travel data (but not already set via options)
    // This handles cases where the data structure has country_destination_info directly
    if (incomingIsMultiCity && countryInfoInData && !hasCountryDestinationInfoOption) {
      setCountryDestinationInfo(countryInfoInData);
      // Clear city-level info when country info is present
      setDestinationInfo(null);
      setIsMultiCityMode(true);
      if (!incomingPlanMode) {
        incomingPlanMode = 'multi_city';
      }
    } else if (incomingIsMultiCity && countryInfoInData && hasCountryDestinationInfoOption) {
      // Data has country info but we also got it from options - prefer options
    } else if (incomingIsMultiCity && !countryInfoInData && !hasCountryDestinationInfoOption) {
      // Multi-city trip but no country info found - this is a problem!
    } else if (!incomingIsMultiCity) {
      // Ensure we don't carry stale multi-city state into a single-city restore
      setCountryDestinationInfo(null);
      setIsMultiCityMode(false);
    }
    // Normalize absent plan_mode: if we have country info, treat as multi-city; otherwise single-city
    if (!incomingPlanMode && !incomingHasMultiCityPlan) {
      if (incomingIsMultiCity) {
        (data as any).plan_mode = 'multi_city';
        incomingPlanMode = 'multi_city';
        setIsMultiCityMode(true);
      } else {
        (data as any).plan_mode = 'single_city';
        setIsMultiCityMode(false);
      }
    } else if (incomingIsMultiCity && incomingPlanMode !== 'multi_city') {
      // If we've inferred multi-city but plan_mode says otherwise, align it
      (data as any).plan_mode = 'multi_city';
      incomingPlanMode = 'multi_city';
    }

    if (hasSearchIdOption) {
      setCurrentSearchId(options?.searchId ?? null);
    }

    setTravelData((prev) => {
      let base = shouldMerge && prev ? prev : null;

      // If previous state was multi-city but incoming data is clearly single-city,
      // drop the multi-city base to avoid carrying over plan_mode/dest info.
      const incomingPlanMode = (data as any)?.plan_mode;
      const incomingHasMultiCityPlan = Boolean((data as any)?.multi_city_plan);
      if (
        base?.plan_mode === 'multi_city' &&
        incomingPlanMode !== 'multi_city' &&
        !incomingHasMultiCityPlan &&
        !incomingIsMultiCity
      ) {
        base = null;
        setIsMultiCityMode(false);
      }

      // If incoming payload is explicitly single-city (or has no multi-city plan),
      // only clear multi-city artifacts when we truly have no country info.
      const shouldClearToSingleCity =
        incomingPlanMode !== 'multi_city' &&
        !incomingHasMultiCityPlan &&
        !incomingIsMultiCity &&
        !countryInfoInData &&
        !hasCountryDestinationInfoOption &&
        !countryDestinationInfo;

      if (shouldClearToSingleCity) {
        (data as any).cities = undefined;
        setCountryDestinationInfo(null);
        setIsMultiCityMode(false);
      }

      // For multi-city trips, preserve country name destination and ignore city-specific destinations
      // For single-city trips, preserve the destination from the first backend response (after normalization)
      if (base?.destination && data.destination) {
        const isMultiCity = base.plan_mode === 'multi_city';
        
        if (isMultiCity) {
          // For multi-city trips, preserve country name destination
          // Ignore individual city destinations (e.g., "Seville, Spain" when destination is already "Spain")
          const baseDestLower = base.destination.toLowerCase();
          const newDestLower = data.destination.toLowerCase();
          
          // If base destination is a country name (no comma or matches country), preserve it
          // If new destination is a city name and base is country, ignore the new one
          const newIsCity = newDestLower.includes(',') && newDestLower !== baseDestLower;
          const baseIsCountry = !baseDestLower.includes(',') || 
                                (base.cities && base.cities.length > 0 && 
                                 baseDestLower === base.cities[0]?.country?.toLowerCase());
          
          if (baseIsCountry && newIsCity) {
            // Base is country name, new is city name - preserve country name
            const { destination: _, ...dataWithoutDestination } = data;
            data = dataWithoutDestination;
          }
        } else {
          // Single-city logic: preserve the destination from the first backend response
          // Only allow destination to be set once, when it's coming from the backend with full format (e.g., "Madrid, Spain")
          // Ignore subsequent updates that might change it to just city name (e.g., "Madrid")
          const prevHasComma = base.destination.includes(',');
          const newHasComma = data.destination.includes(',');

          if (prevHasComma && !newHasComma) {
            // Remove destination from data to prevent override
            const { destination: _, ...dataWithoutDestination } = data;
            data = dataWithoutDestination;
          } else if (prevHasComma && newHasComma && base.destination.toLowerCase() !== data.destination.toLowerCase()) {
            // Both have commas but are different - check if it's a significant change
            const prevCity = base.destination.split(',')[0].trim().toLowerCase();
            const newCity = data.destination.split(',')[0].trim().toLowerCase();

            if (prevCity === newCity) {
              // Same city, just different formatting - preserve the first one
              const { destination: _, ...dataWithoutDestination } = data;
              data = dataWithoutDestination;
            }
          }
        }
      }
      
      // For multi-city trips, if destination is not yet set and we have cities, use country name
      if (!base?.destination && (data as any).plan_mode === 'multi_city' && (data as any).cities?.length > 0) {
        const countryName = (data as any).cities[0]?.country;
        if (countryName) {
          (data as any).destination = countryName;
        }
      }

      // For multi-city trips, merge intelligently to preserve skeleton structure
      const cityKey = (data as any).city_key;
      const city = (data as any).city;
      const isMultiCityBase = base?.plan_mode === 'multi_city';
      const isMultiCityData = (data as any).plan_mode === 'multi_city';
      const isIncrementalUpdate = (data as any).is_incremental === true;
      const hasTravelPlan = Array.isArray(data.travel_plan) && data.travel_plan.length > 0;

      // Use merge function for any multi-city update to preserve skeleton days
      // CRITICAL: If we have a multi-city base, we MUST use merge function to preserve days from other cities
      // This prevents replacing the entire travel_plan when individual city data arrives
      const shouldUseMergeFunction = shouldMerge && (
        (isMultiCityBase && (cityKey || city || hasTravelPlan || isIncrementalUpdate)) ||
        (isMultiCityData && hasTravelPlan)
      );

      const merged = shouldUseMergeFunction
        ? mergeCityDataIntoMultiCity(base, data, cityKey, city)
        : (() => {
            // For non-merge path, if we have a multi-city base and incoming data has travel_plan,
            // we need to preserve existing days to avoid losing other cities' data
            if (isMultiCityBase && hasTravelPlan && base?.travel_plan && data.travel_plan) {
              // Merge travel plans manually to preserve all days
              const baseTravelPlan = base.travel_plan ?? [];
              const existingDays = new Map<number, typeof baseTravelPlan[0]>();
              baseTravelPlan.forEach((day, idx) => {
                const dayNum = day.day_number || idx + 1;
                existingDays.set(dayNum, day);
              });
              
              // Update with incoming days
              data.travel_plan.forEach((day) => {
                const dayNum = day.day_number || data.travel_plan!.indexOf(day) + 1;
                const existingDay = existingDays.get(dayNum);
                
                // Preserve existing day metadata (city, city_key, date) if merging and new day lacks it
                const mergedDay = existingDay ? {
                  ...existingDay,
                  ...day,
                  city: day.city || existingDay.city,
                  city_key: day.city_key || existingDay.city_key,
                  date: day.date || existingDay.date,
                } : day;
                
                existingDays.set(dayNum, mergedDay);
              });
              
              return {
                ...(base ?? {}),
                ...data,
                travel_plan: Array.from(existingDays.values()).sort((a, b) => {
                  const aDay = a.day_number || 0;
                  const bDay = b.day_number || 0;
                  return aDay - bDay;
                }),
              } as TravelData;
            }
            
            // Default: simple merge (may replace travel_plan, but that's OK for single-city or initial load)
            return {
              ...(base ?? {}),
              ...data,
            } as TravelData;
          })();

      // Normalize multi-city destination to country-level to avoid city-specific flashes
      if (merged.plan_mode === 'multi_city') {
        const countryCandidate =
          (merged as any).country_destination_info?.country ||
          merged.country_summary?.country ||
          merged.cities?.[0]?.country;
        if (countryCandidate) {
          const currentDestination = merged.destination || '';
          const looksLikeCity = currentDestination.includes(','); // "City, Country"
          if (!currentDestination || looksLikeCity) {
            merged.destination = countryCandidate;
          }
        }
      } else if (!merged.plan_mode && (merged.cities?.length ?? 0) > 1) {
        merged.plan_mode = 'multi_city';
      }

      // Normalize global dates and day counts for multi-city plans to avoid city-specific overrides
      if (merged.plan_mode === 'multi_city') {
        const validDates = (merged.travel_plan || [])
          .map(day => day?.date)
          .filter((d): d is string => !!d && !Number.isNaN(Date.parse(d)));
        if (validDates.length > 0) {
          const timestamps = validDates.map(d => Date.parse(d));
          const earliest = new Date(Math.min(...timestamps)).toISOString().split('T')[0];
          const latest = new Date(Math.max(...timestamps)).toISOString().split('T')[0];
          merged.start_date = merged.start_date
            ? (new Date(merged.start_date) < new Date(earliest) ? merged.start_date : earliest)
            : earliest;
          merged.end_date = merged.end_date
            ? (new Date(merged.end_date) > new Date(latest) ? merged.end_date : latest)
            : latest;
        } else if (merged.start_date && merged.total_trip_days) {
          const start = new Date(merged.start_date);
          const end = new Date(start);
          end.setDate(end.getDate() + (merged.total_trip_days || 0) - 1);
          merged.end_date = end.toISOString().split('T')[0];
        }

        const planDayCount = merged.travel_plan?.length ?? 0;
        if (!merged.total_trip_days || merged.total_trip_days < planDayCount) {
          merged.total_trip_days = planDayCount;
        }
      }

      // Ensure deleted_activities are properly preserved during merge
      if (shouldMerge && base && Array.isArray(base.deleted_activities) && Array.isArray(data.deleted_activities)) {
        // Merge deleted_activities arrays, removing duplicates by ID
        const deletedMap = new Map();
        [...(base.deleted_activities || []), ...(data.deleted_activities || [])].forEach(item => {
          if (item?.id) {
            deletedMap.set(item.id, item);
          }
        });
        merged.deleted_activities = Array.from(deletedMap.values());
      } else if (shouldMerge && base && Array.isArray(base.deleted_activities) && !data.deleted_activities) {
        // Preserve base deleted_activities if data doesn't have any
        merged.deleted_activities = base.deleted_activities;
      }


      setSections(buildPlannerSection(merged, activityPhotos));
      setRestaurants(buildRestaurants(merged, activityPhotos));
      setSummary(buildSummary(merged));
      const multiCityDetected = merged.plan_mode === 'multi_city' || (merged.cities?.length ?? 0) > 1 || Boolean((data as any).multi_city_plan);
      setIsMultiCityMode(multiCityDetected);

      return merged;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityPhotos]);

  // Update summary with destination description when available
  React.useEffect(() => {
    const isMultiCityTrip = travelData?.plan_mode === 'multi_city' || (travelData?.cities?.length ?? 0) > 1;
    const summaryInfo = isMultiCityTrip ? countryDestinationInfo : destinationInfo;

    if (summaryInfo?.description) {
      setSummary((prev) => (prev ? { ...prev, overview: summaryInfo.description } : prev));
    }
  }, [destinationInfo, countryDestinationInfo, travelData?.plan_mode, travelData?.cities?.length]);

  // Navigate to /trip when destination info starts loading during active generation
  React.useEffect(() => {
    // Navigate as soon as destination info starts loading OR when we have destination data
    const shouldNavigate = pendingNavigationRef.current &&
                           location.pathname !== '/trip' &&
                           (isDestinationInfoLoading || destinationInfo !== null);

    if (shouldNavigate) {
      navigate('/trip', { replace: true });
      pendingNavigationRef.current = false;
    }
  }, [isDestinationInfoLoading, destinationInfo, location.pathname, navigate]);

  // Update destination with normalized version from API when available
  React.useEffect(() => {
    if (destinationInfo?.normalized_destination && travelData?.destination) {
      const normalizedDest = destinationInfo.normalized_destination.trim();
      const currentDest = travelData.destination.trim();

      // Skip for multi-city trips - they use country name as destination, not city-specific format
      // Updating to city format would cause infinite refresh loops as destination no longer matches
      if (travelData.plan_mode === 'multi_city') {
        return;
      }

      // Only update if the normalized destination is different and properly formatted (has comma for "City, Country")
      if (normalizedDest !== currentDest && normalizedDest.includes(',')) {
        setTravelData((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, destination: normalizedDest };
          setSummary(buildSummary(updated));
          return updated;
        });
      }
    }
  }, [destinationInfo?.normalized_destination, travelData?.destination, travelData?.plan_mode]);

  // Ensure the active plan URL reflects the latest search and is shareable
  React.useEffect(() => {
    const isPlannerRoute = location.pathname.startsWith('/plan/');
    const isTripRoute = location.pathname === '/trip' || location.pathname.startsWith('/trip/');

    if (!currentSearchId) {
      if (location.pathname.startsWith('/trip/') && location.pathname !== '/trip') {
        navigate('/trip', { replace: true });
      }

      try {
        localStorage.removeItem('tripgen_nav_current_plan');
      } catch (stashError) {
        plannerLogger.error('Error clearing stashed plan URL:', stashError);
      }
      return;
    }

    if (
      !travelData?.destination ||
      !travelData.start_date ||
      !travelData.end_date
    ) {
      return;
    }

    const sharedPath = `/trip/${currentSearchId}`;

    if (!isPlannerRoute && !isTripRoute) {
      try {
        localStorage.setItem('tripgen_nav_current_plan', sharedPath);
      } catch (stashError) {
        plannerLogger.error('Error stashing plan URL:', stashError);
      }
      return;
    }

    if (location.pathname !== sharedPath) {
      navigate(sharedPath, { replace: true });
    }

    try {
      localStorage.setItem('tripgen_nav_current_plan', sharedPath);
    } catch (stashError) {
      plannerLogger.error('Error stashing plan URL:', stashError);
    }
  }, [travelData?.destination, travelData?.start_date, travelData?.end_date, currentSearchId, location.pathname, navigate]);

  const toggleExperienceSelection = React.useCallback((id: string) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        experiences: section.experiences.map((experience) =>
          experience.id === id ? { ...experience, isSelected: !experience.isSelected } : experience,
        ),
      })),
    );

    setRestaurants((prev) =>
      prev.map((restaurant) => (restaurant.id === id ? { ...restaurant, isSelected: !restaurant.isSelected } : restaurant)),
    );
  }, []);

  const toggleAll = React.useCallback((checked: boolean) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        experiences: section.experiences.map((experience) => ({ ...experience, isSelected: checked })),
      })),
    );

    setRestaurants((prev) => prev.map((restaurant) => ({ ...restaurant, isSelected: checked })));
  }, []);

  const openExperience = React.useCallback((experience: PlannerExperience | null) => {
    setActiveExperience(experience);
  }, []);

  // Keep activeExperience in sync when the underlying data updates (e.g., when photos load)
  React.useEffect(() => {
    if (!activeExperience) return;

    // Look for the updated version of the active experience in sections or restaurants
    let updated: PlannerExperience | undefined;
    const activeId = activeExperience.id;

    for (const section of sections) {
      updated = section.experiences.find((exp): exp is PlannerExperience => exp.id === activeId);
      if (updated && updated !== activeExperience) {
        // Update activeExperience if it's a different object (which happens when photos load)
        setActiveExperience(updated);
        return;
      }
      if (updated) {
        return;
      }
    }

    // Check restaurants if not found in sections
    updated = restaurants.find((exp): exp is PlannerExperience => exp.id === activeId);
    if (updated && updated !== activeExperience) {
      // Update activeExperience if it's a different object
      setActiveExperience(updated);
    }
  }, [sections, restaurants, activeExperience]);

  const resetConversation = React.useCallback(() => {
    setMessages([]);
    setError(null);
    setLoadingPhase(null);
  }, []);

  const resetPlanView = React.useCallback(() => {
    setTravelData(null);
    setSections([]);
    setRestaurants([]);
    setSpecialActivities({});
    setSummary(null);
    setActiveExperience(null);
    setDestinationImageUrls([]);
    setDestinationInfoRaw(null);
    setCountryDestinationInfo(null);
    setDestinationInfoError(null);
    setIsDestinationInfoStreaming(false);
    setIsDestinationInfoFetching(false);
    setIsMultiCityMode(false);
    destinationInfoFetchRef.current = null;
  }, []);

  // Full in-memory reset of the current plan (without touching chats)
  const resetInMemoryPlanState = React.useCallback((clearMessages = false) => {
    setTravelData(null);
    setSections([]);
    setRestaurants([]);
    setSpecialActivities({});
    setSummary(null);
    setActiveExperience(null);
    if (clearMessages) {
      setMessages([]);
    }
    setActivityPhotos({});
    setDestinationImageUrls([]);
    setDestinationInfoRaw(null);
    setCountryDestinationInfo(null);
    setDestinationInfoError(null);
    setIsDestinationInfoStreaming(false);
    setIsDestinationInfoFetching(false);
    setIsMultiCityMode(false);
    destinationInfoFetchRef.current = null;
  }, []);

  const resetPlanner = React.useCallback(async () => {
    // Clear all planner state to return to blank form
    // Clear the navigation stash when explicitly resetting
    try {
      localStorage.removeItem('tripgen_nav_current_plan');
      await persistentCache.delete(PLANNER_STATE_KEY);
    } catch (error) {
      plannerLogger.error('Error clearing navigation stash:', error);
    }
    setTravelData(null);
    setSections([]);
    setRestaurants([]);
    setSpecialActivities({});
    setSummary(null);
    setActiveExperience(null);
    setMessages([]);
    setError(null);
    setLoading(false);
    setLoadingPhase(null);
    setCurrentSearchId(null);
    setDestinationInfoRaw(null);
    setCountryDestinationInfo(null);
    setDestinationInfoError(null);
    setIsDestinationInfoStreaming(false);
    setIsDestinationInfoFetching(false);
    setIsMultiCityMode(false);
    destinationInfoFetchRef.current = null;
  }, []);

  const clearCurrentPlan = React.useCallback(async () => {
    // Clear current plan data and navigation stash (used before generating new plan)
    setCurrentSearchId(null);
    setDestinationImageUrls([]); // Clear destination images when starting new plan
    resetInMemoryPlanState(false);
    try {
      localStorage.removeItem('tripgen_nav_current_plan');
      await persistentCache.delete(PLANNER_STATE_KEY);
    } catch (error) {
      plannerLogger.error('Error clearing navigation stash:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDestinationInfoStream = React.useCallback(() => {
    setDestinationInfoRaw(null);
    setCountryDestinationInfo(null);
    setDestinationImageUrls([]);
    setDestinationInfoError(null);
    setIsDestinationInfoStreaming(true);
    setIsDestinationInfoFetching(false);
    setIsMultiCityMode(false);
    destinationInfoFetchRef.current = null;
  }, []);

  const startNewChat = React.useCallback(async (): Promise<string | null> => {
    if (creatingChatRef.current) {
      return currentChatId;
    }
    creatingChatRef.current = true;
    try {
      await resetPlanner();
      const response = await apiCreateChat({
        session_id: generateSessionId(),
      });
      const newChatId = response.chat.chat_id;
      setCurrentChatId(newChatId);
      setAttachedTrips([]);
      setActiveChatTripId(null);
      await refreshChats();
      return newChatId;
    } catch (error) {
      plannerLogger.error('Failed to start new chat:', error);
      return null;
    } finally {
      creatingChatRef.current = false;
    }
  }, [currentChatId, refreshChats, resetPlanner]);

  const ensureActiveChat = React.useCallback(async (): Promise<string | null> => {
    if (currentChatId) {
      return currentChatId;
    }
    if (chats.length > 0) {
      const firstChatId = chats[0].chat_id;
      setCurrentChatId(firstChatId);
      return firstChatId;
    }
    return await startNewChat();
  }, [chats, currentChatId, startNewChat]);

  const selectChat = React.useCallback(
    async (chatId: string) => {
      if (!chatId || chatId === currentChatId) {
        return;
      }
      setCurrentChatId(chatId);
      setCurrentSearchId(null);
      setTravelData(null);
      setSections([]);
      setRestaurants([]);
      setSpecialActivities({});
      setSummary(null);
      setActiveExperience(null);
      setDestinationImageUrls([]);
      setMessages([]);
      setError(null);
      setAttachedTrips([]);
      setActiveChatTripId(null);
    },
    [currentChatId],
  );

  const persistChatMessage = React.useCallback(
    async (
      content: string,
      role: 'user' | 'assistant',
      metadata?: McpToolMetadata[],
      chatIdOverride?: string | null,
      tripCard?: PlannerMessage['tripCard'],
    ) => {
      const chatId = chatIdOverride ?? (await ensureActiveChat());
      if (!chatId) return;
      try {
        const messageMetadata: any = metadata ? { mcp_metadata: metadata } : {};
        if (tripCard) {
          messageMetadata.trip_card = tripCard;
        }
        await apiCreateMessage(chatId, {
          chat_id: chatId,
          role,
          content,
          trip_context_id: currentSearchId || undefined,
          metadata: Object.keys(messageMetadata).length > 0 ? messageMetadata : undefined,
        });
        refreshChats();
      } catch (error) {
        plannerLogger.error('Failed to persist chat message:', error);
      }
    },
    [currentSearchId, ensureActiveChat, refreshChats],
  );

  // Append a trip card message after a plan has been saved with an ID
  React.useEffect(() => {
    if (!currentSearchId || !travelData?.travel_plan?.length) {
      return;
    }
    if (deliveredTripCardsRef.current.has(currentSearchId)) {
      return;
    }

    const days = travelData.total_trip_days ?? travelData.travel_plan.length;
    const activities =
      travelData.activities?.length ||
      travelData.travel_plan.reduce(
        (total, day) => total + (day.activities?.length || 0),
        0,
      );
    const photoUrl =
      destinationInfo?.image_urls?.[0] ||
      destinationInfo?.image_url ||
      countryDestinationInfo?.image_urls?.[0] ||
      countryDestinationInfo?.image_url ||
      null;

    const tripCardData = {
      searchId: currentSearchId,
      destination: travelData.destination || 'Your trip',
      startDate: travelData.start_date,
      endDate: travelData.end_date,
      days,
      activities,
      photoUrl,
    };

    deliveredTripCardsRef.current.add(currentSearchId);
    appendMessage({
      text: 'Trip saved',
      isUser: false,
      tripCard: tripCardData,
    });

    // Persist the trip card message to the database
    void persistChatMessage('Trip saved', 'assistant', undefined, undefined, tripCardData);
  }, [
    appendMessage,
    countryDestinationInfo?.image_url,
    countryDestinationInfo?.image_urls,
    currentSearchId,
    destinationInfo?.image_url,
    destinationInfo?.image_urls,
    travelData?.destination,
    travelData?.end_date,
    travelData?.start_date,
    travelData?.total_trip_days,
    travelData?.travel_plan,
    travelData?.activities,
    persistChatMessage,
  ]);

  const attachTripToCurrentChat = React.useCallback(
    async (searchId: string) => {
      const chatId = await ensureActiveChat();
      if (!chatId) return;
      try {
        const response = await attachTripToChat(chatId, {
          chat_id: chatId,
          search_id: searchId,
          set_as_active: true,
        });
        setAttachedTrips(response.attached_trips || []);
        setActiveChatTripId(response.active_trip_id ?? searchId);
        // Reload chat details to ensure state is in sync
        await loadChatDetails(chatId);
        refreshChats();
    } catch (error) {
      plannerLogger.error('Failed to attach trip to chat:', error);
    }
  },
  [ensureActiveChat, refreshChats, loadChatDetails],
  );

  const restoreTripForChat = React.useCallback(
    async (chatId: string | null, searchId: string | null) => {
      if (!searchId || !chatId) return;

      const lockedSearchId = sharedTripLockRef.current;
      if (lockedSearchId && lockedSearchId !== searchId) {
        plannerLogger.info('Skipping chat restore because a shared trip lock is active', {
          lockedSearchId,
          requestedSearchId: searchId,
        });
        return;
      }

      const signature = `${chatId}:${searchId}`;
      const alreadyRestored =
        lastRestoredChatRef.current === chatId &&
        lastRestoredTripRef.current === searchId &&
        currentSearchId === searchId &&
        travelData !== null;

      if (alreadyRestored || restoringActiveTripRef.current === signature) {
        return;
      }

      restoringActiveTripRef.current = signature;

      try {
        const result = await restoreSearch(searchId);
        if (!result?.travel_state) {
          plannerLogger.warn('No travel_state returned when restoring trip for chat', { searchId });
          return;
        }

        const restoredSearchId = result.search_history?.search_id ?? searchId;
        const isMultiCity =
          result.travel_state?.plan_mode === 'multi_city' ||
          (result.travel_state?.cities?.length ?? 0) > 1 ||
          Boolean((result as any)?.multi_city_plan) ||
          Boolean(result.country_destination_info || (result.travel_state as any)?.country_destination_info);

        const countryInfo = isMultiCity
          ? (result.country_destination_info || (result.travel_state as any)?.country_destination_info || null)
          : null;
        const cityInfo = !isMultiCity
          ? (result.destination_info || (result.travel_state as any)?.destination_info || null)
          : (countryInfo ? null : (result.destination_info || (result.travel_state as any)?.destination_info || null));

        hydrateFromTravelData(result.travel_state, {
          merge: false,
          searchId: restoredSearchId,
          destinationInfo: cityInfo,
          countryDestinationInfo: countryInfo,
        });

        setActiveChatTripId(restoredSearchId);
        lastRestoredChatRef.current = chatId;
        lastRestoredTripRef.current = restoredSearchId;
      } catch (error) {
        plannerLogger.error('Failed to restore trip for chat', error);
      } finally {
        restoringActiveTripRef.current = null;
      }
    },
    [currentSearchId, hydrateFromTravelData, travelData],
  );

  const markTripAsActive = React.useCallback(
    async (searchId: string) => {
      const chatId = await ensureActiveChat();
      if (!chatId) return;
      try {
        const response = await apiSetActiveTrip(chatId, searchId);
        const activeId = response.active_trip_id ?? searchId;
        setActiveChatTripId(activeId);
        await restoreTripForChat(chatId, activeId);
        loadChatDetails(chatId);
        refreshChats();
      } catch (error) {
        plannerLogger.error('Failed to set active trip:', error);
      }
    },
    [ensureActiveChat, loadChatDetails, refreshChats, restoreTripForChat],
  );

  const applySaveResult = React.useCallback(
    async (saveResult?: TripSaveResult | null): Promise<string | null> => {
      const savedSearchId = saveResult?.search_id;
      if (!savedSearchId) {
        return null;
      }

      // Keep the latest saved version in local state
      setCurrentSearchId(savedSearchId);

      if (saveResult?.status === 'failed') {
        return savedSearchId;
      }

      if (activeChatTripId === savedSearchId) {
        return savedSearchId;
      }

      try {
        await attachTripToCurrentChat(savedSearchId);
      } catch (error) {
        plannerLogger.error('Failed to attach updated trip to chat', error);
      }

      return savedSearchId;
    },
    [activeChatTripId, attachTripToCurrentChat],
  );

  // Automatically hydrate the active trip when switching chats or when active trip changes
  React.useEffect(() => {
    if (!currentChatId || !activeChatTripId) {
      return;
    }

    const lockedSearchId = sharedTripLockRef.current;
    if (lockedSearchId && lockedSearchId !== activeChatTripId) {
      return;
    }

    // CRITICAL: Don't restore old trip while a new search is actively generating
    // This prevents showing old destination data (e.g., Yerevan) when user is searching for a new one (e.g., Vienna)
    if (loading || isGeneratingTripPlan) {
      return;
    }

    const chatChanged = lastRestoredChatRef.current !== currentChatId;
    const tripChanged = lastRestoredTripRef.current !== activeChatTripId;

    if (!chatChanged && !tripChanged) {
      return;
    }

    void (async () => {
      await restoreTripForChat(currentChatId, activeChatTripId);
    })();
  }, [currentChatId, activeChatTripId, restoreTripForChat, loading, isGeneratingTripPlan]);

  const handleSearchSaved = React.useCallback(
    async (searchId: string) => {
      await applySaveResult({ search_id: searchId, status: 'created' });

      if (currentChatId && travelData?.destination) {
        try {
          await apiUpdateChat(currentChatId, { title: travelData.destination });
          refreshChats();
        } catch (error) {
          plannerLogger.error('Failed to update chat title:', error);
        }
      }
    },
    [applySaveResult, currentChatId, travelData?.destination, refreshChats],
  );

  const generatePlan = React.useCallback(
    async (tripParameters: TripParameters, options?: PlanRequestOptions) => {
      const confirmCreation = options?.confirmCreation ?? false;
      const skipUserMessage = options?.skipUserMessage === true;
      let navigationTriggered = location.pathname === '/trip';
      const destinationInput = tripParameters.destination?.trim() || '';
      const previousDestination = travelData?.destination?.trim().toLowerCase();
      const normalizedDestination = destinationInput.toLowerCase();
      const shouldResetState =
        !travelData ||
        (destinationInput && previousDestination !== normalizedDestination) ||
        (tripParameters.startDate && travelData?.start_date && travelData.start_date !== tripParameters.startDate) ||
        (tripParameters.endDate && travelData?.end_date && travelData.end_date !== tripParameters.endDate);
      const stateForRequest = shouldResetState ? undefined : travelData;

      const userMessage = tripParameters.destination
        ? `Plan a trip to ${tripParameters.destination}`
        : 'Generate a new travel plan';

      // Clear previous plan if generating a new one
      setCurrentSearchId(null);

      if (shouldResetState) {
        startDestinationInfoStream();
        await clearCurrentPlan();
        resetInMemoryPlanState(false);
      } else {
        try {
          localStorage.removeItem('tripgen_nav_current_plan');
        } catch (error) {
          plannerLogger.error('Error clearing navigation stash:', error);
        }
        startDestinationInfoStream();
      }

      // Make the UI respond immediately while we spin up the backend chat
      setLoading(true);
      setError(null);
      setLoadingPhase('initializing');
      setIsGeneratingTripPlan(true); // generatePlan is always a trip plan generation

      // Jump to /trip right away so users aren't blocked by network latency
      if (confirmCreation) {
        if (!navigationTriggered && location.pathname !== '/trip' && location.pathname !== '/chat') {
          navigate('/trip', { replace: true });
          navigationTriggered = true;
        }
        // If navigation already happened, no need to keep the pending flag
        pendingNavigationRef.current = !navigationTriggered;
      } else {
        pendingNavigationRef.current = false;
      }

      const chatId = await ensureActiveChat();
      if (!chatId) {
        setError('Unable to start a chat session. Please try again.');
        setIsDestinationInfoStreaming(false);
        setLoading(false);
        setLoadingPhase(null);
        setIsGeneratingTripPlan(false);
        pendingNavigationRef.current = false;
        return;
      }

      if (!skipUserMessage) {
        appendMessage({ text: userMessage, isUser: true });
        await persistChatMessage(userMessage, 'user', undefined, chatId);
      }

      // Navigation will happen when destination info (description) is loaded
      // See useEffect that watches destinationInfo?.description

      if (destinationInput && shouldResetState && !skipUserMessage) {
        hydrateFromTravelData(
          {
            destination: destinationInput,
            start_date: tripParameters.startDate,
            end_date: tripParameters.endDate,
            budget: tripParameters.budget || undefined,
            plan_mode: 'single_city',
            travel_plan: [],
            activities: [],
            overflow_activities: [],
            total_trip_days: tripParameters.numberOfDays,
            additional_info: [],
            safety_notes: [],
            packing_list: [],
          },
          { merge: false },
        );

        // CRITICAL: Fetch destination info immediately when starting new trip
        // This triggers a separate, instant API call for destination info
        // instead of waiting for it to come from the main travel plan stream
        void getDestinationInfo(destinationInput)
          .then((info) => {
            setDestinationInfoRaw(info);
          })
          .catch((error: any) => {
            plannerLogger.error('Failed to fetch destination info:', error);
          });
      }

      try {
        const reader = await generateTravelPlan(
          tripParameters,
          stateForRequest,
          chatId,
          confirmCreation
        );
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          buffer += chunk;
          let lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonString = line.slice(6).trim();
            if (!jsonString) continue;

            try {
              const data = JSON.parse(jsonString);

              if (confirmCreation && !navigationTriggered && data.type !== 'confirmation_required') {
                if (location.pathname !== '/trip') {
                  navigate('/trip', { replace: true });
                }
                navigationTriggered = true;
                pendingNavigationRef.current = false;
              }

              if (data.type === 'error') {
                const errorMessage = data.data || 'Failed to generate travel plan';
                const isRetryable = data.retryable === true;

                setError(errorMessage);
                setLoading(false);
                setLoadingPhase(null);
                setIsDestinationInfoStreaming(false);
                pendingNavigationRef.current = false; // Reset navigation flag on error

                // Add helpful retry message for overload errors
                const userFacingMessage = isRetryable
                  ? `${errorMessage}\n\nPlease try again in a moment. The system will automatically retry if needed.`
                  : errorMessage;

                appendMessage({ text: userFacingMessage, isUser: false });
                return;
              }

              if (data.type === 'confirmation_required') {
                const prompt = data.data?.message || 'Should I proceed with this request?';
                const confirmation: ConfirmationPrompt = {
                  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `confirm-${Date.now()}`,
                  prompt,
                  source: 'initial',
                  tripParameters,
                };
                appendMessage({ text: prompt, isUser: false, confirmation });
                void persistChatMessage(prompt, 'assistant', undefined, chatId);
                setLoading(false);
                setLoadingPhase(null);
                setIsDestinationInfoStreaming(false);
                setIsGeneratingTripPlan(false);
                pendingNavigationRef.current = false;
                return;
              }

              if (data.type === 'message') {
                appendMessage({
                  text: data.data,
                  isUser: false,
                  mcpMetadata: data.mcp_metadata || undefined
                });
                if (data.data) {
                  void persistChatMessage(data.data, 'assistant', data.mcp_metadata || undefined, chatId);
                }
              }

              if (data.type === 'router_decision') {
                setLoadingPhase('activities_coordination');
              }

              if (data.type === 'intermediate_response') {
                const phase = data.phase || null;
                setLoadingPhase(phase);

                // Detect if this is a multi-city partial update (from individual city)
                const hasMultiCityPlan = Boolean(data.data?.multi_city_plan);
                const explicitMultiCity = Boolean(
                  hasMultiCityPlan ||
                  data.data?.plan_mode === 'multi_city' ||
                  data.plan_mode === 'multi_city' ||
                  travelData?.plan_mode === 'multi_city'
                );
                const isMultiCityContext = explicitMultiCity || isMultiCityMode;
                if (explicitMultiCity) {
                  setIsMultiCityMode(true);
                } else if (!hasMultiCityPlan) {
                  setIsMultiCityMode(false);
                }
                const cityKey = data.city_key || data.data?.city_key;
                const city = data.city || data.data?.city;
                const isCitySpecific = Boolean(cityKey || city);
                const isMergedMultiCityUpdate = hasMultiCityPlan || phase === 'multi_city_skeleton';
                const shouldIgnoreCityUpdate = isMultiCityContext && isCitySpecific && !isMergedMultiCityUpdate;


                if (phase === 'destination_info') {
                  // CRITICAL: Backend sends country info under 'country_destination_info' key
                  // and city info under 'destination_info' key
                  const streamedCityInfo = data.data?.destination_info;
                  const streamedCountryInfo = data.data?.country_destination_info;
                  const scope = data.data?.scope;
                  const planMode = data.data?.plan_mode || data.plan_mode;


                  if (planMode === 'multi_city') {
                    setIsMultiCityMode(true);
                  }

                  // Handle country destination info (for multi-city trips)
                  if (streamedCountryInfo) {
                    setCountryDestinationInfo(streamedCountryInfo);
                    setIsMultiCityMode(true);
                    // Clear city-level destination info when country info arrives
                    if (isMultiCityContext || planMode === 'multi_city') {
                      setDestinationInfoRaw(null);
                    }
                    setDestinationInfoError(null);
                  }

                  // Handle city destination info (for single-city trips)
                  if (streamedCityInfo) {
                    if (scope === 'country' || isMultiCityContext || planMode === 'multi_city') {
                      // In multi-city mode, ignore city-level destination_info updates
                    } else {
                      // City-level destination info for single-city trips
                      setDestinationInfoRaw(streamedCityInfo);
                      setDestinationInfoError(null);
                    }
                  }

                  setIsDestinationInfoStreaming(false);
                }

                if (phase === 'country_summary') {
                  const countrySummary = data.data?.country_summary;
                  if (countrySummary) {
                    const isMultiCity = travelData?.plan_mode === 'multi_city' || countrySummary.country;
                    if (isMultiCity) {
                      setIsMultiCityMode(true);
                    }
                    // Clear city-level destination info when multi-city mode is detected
                    if (isMultiCity) {
                      setDestinationInfoRaw(null);
                    }
                    setTravelData((prev) => {
                      if (!prev) return prev;
                      // For multi-city trips, set destination to country name if not already set or if currently a city name
                      let destination = prev.destination;
                      if (isMultiCity && countrySummary.country) {
                        const countryLower = countrySummary.country.toLowerCase();
                        const destLower = (prev.destination || '').toLowerCase();
                        // If destination is not set, or it's a city name (contains comma), set it to country name
                        if (!prev.destination || (destLower.includes(',') && !destLower.includes(countryLower))) {
                          destination = countrySummary.country;
                        }
                      }
                      return {
                        ...prev,
                        country_summary: countrySummary,
                        destination,
                        plan_mode: isMultiCity ? 'multi_city' : prev.plan_mode,
                      };
                    });
                  }
                }

                // Handle multi-city skeleton - shows trip structure with all days/cities immediately
                if (phase === 'multi_city_skeleton') {
                  const skeletonState = data.data?.state;
                  if (skeletonState) {
                    setIsMultiCityMode(true);
                    const travelDataUpdate: Partial<TravelData> = {
                      ...skeletonState,
                      plan_mode: 'multi_city',
                      cities: data.data?.cities,
                      is_preliminary: true,
                    };
                    // Check if destination changed - if so, don't merge with old data
                    const shouldMerge = travelData?.destination?.toLowerCase() === skeletonState.destination?.toLowerCase();
                    hydrateFromTravelData(travelDataUpdate, { merge: shouldMerge });
                  }
                }

                // Process activities_preliminary updates
                // For multi-city: ONLY process merged incremental updates (is_incremental: true)
                // Skip individual city events to avoid chaotic re-renders
                if (phase === 'activities_preliminary') {
                  if (!shouldIgnoreCityUpdate) {
                    const preliminaryState = normalizePreliminaryState(data.data);
                    if (preliminaryState) {
                      // Check if destination changed - if so, don't merge with old data
                      const shouldMerge = travelData?.destination?.toLowerCase() === preliminaryState.destination?.toLowerCase();
                      hydrateFromTravelData(preliminaryState, { merge: shouldMerge });
                    }
                  }
                }

                if (phase === 'special_activities') {
                  const itemsByDay = data.data?.itemsByDay;
                  if (itemsByDay && typeof itemsByDay === 'object') {
                    setSpecialActivities(itemsByDay);
                  }
                }

                // Process state updates
                // For multi-city: ONLY process merged incremental updates (is_incremental: true)
                // Skip individual city events to avoid chaotic re-renders
                const stateUpdate = data.data?.state;
                if (stateUpdate) {
                  if (!shouldIgnoreCityUpdate) {
                    // Check if destination changed - if so, don't merge with old data
                    const shouldMerge = travelData?.destination?.toLowerCase() === stateUpdate.destination?.toLowerCase();
                    hydrateFromTravelData(stateUpdate, { merge: shouldMerge });
                  }
                }
              }

              if (data.type === 'plan_response') {
                const isCitySpecific = Boolean(data.city || data.city_key || data.data?.city || data.data?.city_key);
                const hasMultiCityPlan = Boolean(data.data?.multi_city_plan);
                const explicitMultiCity = Boolean(
                  hasMultiCityPlan ||
                  data.data?.plan_mode === 'multi_city' ||
                  data.plan_mode === 'multi_city' ||
                  travelData?.plan_mode === 'multi_city'
                );
                const isMultiCityContext = explicitMultiCity || isMultiCityMode;
                if (explicitMultiCity) {
                  setIsMultiCityMode(true);
                }

                // For multi-city trips, ignore city-level plan responses and wait for merged/final payload
                if (isMultiCityContext && isCitySpecific && !hasMultiCityPlan) {
                  continue;
                }

                setLoadingPhase(null);
                const responseData = data.data;
                if (responseData?.destination) {
                  // CRITICAL: Check if destination changed - don't merge old trip data with new trip!
                  const destinationChanged = travelData?.destination && responseData.destination &&
                    travelData.destination.toLowerCase() !== responseData.destination.toLowerCase();
                  hydrateFromTravelData(responseData, { merge: !destinationChanged });
                  const planReadyMessage = 'Here is your curated itinerary. Let me know if you want tweaks.';
                  appendMessage({
                    text: planReadyMessage,
                    isUser: false,
                  });
                  void persistChatMessage(planReadyMessage, 'assistant', undefined, chatId);
                }
                setLoading(false);
              }

              if (data.type === 'final_response') {
                setLoadingPhase(null);
                const finalDestinationInfo = data.data?.destination_info;
                const responseCountryInfo = data.data?.country_destination_info;


                // CRITICAL: Only check indicators from THE CURRENT RESPONSE, not from stale state
                // Otherwise we'll incorrectly treat new single-city trips as multi-city
                const isMultiCityFinal = data.data?.plan_mode === 'multi_city' ||
                                        Boolean(responseCountryInfo) ||
                                        Boolean(data.data?.cities?.length > 0);


                // For multi-city trips, ONLY set country info
                // For single-city trips, ONLY set city info
                if (isMultiCityFinal) {
                  // Multi-city trip: only set country info, clear any city info
                  if (responseCountryInfo) {
                    setCountryDestinationInfo(responseCountryInfo);
                    setIsMultiCityMode(true);
                  } else {
                    // console.warn('⚠️ [FINAL_RESPONSE] Multi-city trip but no country_destination_info!');
                  }
                  setDestinationInfo(null);
                  if (finalDestinationInfo) {
                    // console.warn('⚠️ [FINAL_RESPONSE] Ignoring city-level destination_info for multi-city trip');
                  }
                } else {
                  // Single-city trip: set city info, clear any stale country info
                  if (finalDestinationInfo) {
                    setDestinationInfoRaw(finalDestinationInfo);
                    setDestinationInfoError(null);
                  }
                  setCountryDestinationInfo(null);
                }
                setIsDestinationInfoStreaming(false);
                const finalState = data.data?.state;
                if (finalState) {
                  // Include multi-city fields in travel data
                  const planMode = data.data?.plan_mode || (responseCountryInfo ? 'multi_city' : 'single_city');
                  const travelDataUpdate: Partial<TravelData> = {
                    ...finalState,
                    plan_mode: planMode,
                    cities: data.data?.cities,
                    country_summary: data.data?.country_summary || finalState.country_summary,
                    country_destination_info: responseCountryInfo || finalState.country_destination_info || countryDestinationInfo,
                  };
                  // CRITICAL: Check if destination changed - don't merge old trip data with new trip!
                  const destinationChanged = travelData?.destination && finalState.destination &&
                    travelData.destination.toLowerCase() !== finalState.destination.toLowerCase();
                  hydrateFromTravelData(travelDataUpdate, { merge: !destinationChanged });
                  const completionMessage = 'All set! Your itinerary is ready below.';
                  appendMessage({
                    text: completionMessage,
                    isUser: false,
                  });
                  void persistChatMessage(completionMessage, 'assistant', undefined, chatId);
                }
                setLoading(false);
              }

              if (data.type === 'search_saved') {
                const searchId = data.data?.search_id;
                if (searchId) {
                  void handleSearchSaved(searchId);
                }
              }
            } catch (streamError) {
              plannerLogger.error('Failed to parse streaming chunk', streamError);
            }
          }
        }
      } catch (err: any) {
        plannerLogger.error('generatePlan error:', err);
        setError(err?.message || 'Failed to connect to the planning service.');
        pendingNavigationRef.current = false; // Reset navigation flag on error
      } finally {
        setLoading(false);
        setLoadingPhase(null);
        setIsDestinationInfoStreaming(false);
        setIsGeneratingTripPlan(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendMessage, hydrateFromTravelData, travelData, navigate, clearCurrentPlan, ensureActiveChat, persistChatMessage, handleSearchSaved],
  );

  const sendFollowUp = React.useCallback(
    async (message: string, options?: FollowUpOptions) => {
      const confirmCreation = options?.confirmCreation ?? false;
      const skipUserMessage = options?.skipUserMessage === true;
      if (!message.trim()) return;

      // Set loading immediately so UI updates right away
      setLoading(true);
      setError(null);
      setLoadingPhase('initializing');

      const chatId = await ensureActiveChat();
      if (!chatId) {
        setError('Unable to start a chat session. Please try again.');
        setLoading(false);
        setLoadingPhase(null);
        return;
      }

      if (!skipUserMessage) {
        appendMessage({ text: message, isUser: true });
        await persistChatMessage(message, 'user', undefined, chatId);
      }

      try {
        // Format conversation history for backend
        const formattedHistory = messages.map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          content: msg.text,
          timestamp: msg.timestamp
        }));

        const reader = await sendFollowUpMessage(
          message,
          travelData || undefined,
          formattedHistory,
          chatId,
          confirmCreation,
          currentSearchId
        );
        const decoder = new TextDecoder();
        let buffer = '';
        let hasCompletedResponse = false; // Track if we've already sent completion message
        let isNewTripRequest = false; // Track if router determined this is a new trip (PLAN query)

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          buffer += chunk;
          let lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonString = line.slice(6).trim();
            if (!jsonString) continue;

            try {
              const data = JSON.parse(jsonString);

              if (data.type === 'error') {
                const errorMessage = data.data || 'Follow-up failed.';
                const isRetryable = data.retryable === true;

                setError(errorMessage);
                setLoading(false);
                setLoadingPhase(null);
                setIsDestinationInfoStreaming(false);

                // Add helpful retry message for overload errors
                const userFacingMessage = isRetryable
                  ? `${errorMessage}\n\nPlease try again in a moment. The system will automatically retry if needed.`
                  : errorMessage;

                appendMessage({ text: userFacingMessage, isUser: false });
                return;
              }

              if (data.type === 'confirmation_required') {
                const prompt = data.data?.message || 'Should I proceed with this request?';
                const confirmation: ConfirmationPrompt = {
                  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `confirm-${Date.now()}`,
                  prompt,
                  source: 'followup',
                  retryMessage: message,
                };
                appendMessage({ text: prompt, isUser: false, confirmation });
                void persistChatMessage(prompt, 'assistant', undefined, chatId);
                setLoading(false);
                setLoadingPhase(null);
                setIsDestinationInfoStreaming(false);
                setIsGeneratingTripPlan(false);
                return;
              }

              if (data.type === 'message') {
                appendMessage({
                  text: data.data,
                  isUser: false,
                  mcpMetadata: data.mcp_metadata || undefined
                });
                if (data.data) {
                  void persistChatMessage(data.data, 'assistant', data.mcp_metadata || undefined, chatId);
                }
              }

              if (data.type === 'router_decision') {
                const routerDecision = data.data;
                const isPlanQuery = routerDecision?.query_type?.toLowerCase() === 'plan';

                // When confirmation is pending, don't navigate or clear anything yet
                if (!confirmCreation && isPlanQuery) {
                  isNewTripRequest = true;
                  continue;
                }

                setLoadingPhase('activities_coordination');

                // Check if router determined this is a PLAN query (new trip request)
                isNewTripRequest = isPlanQuery;
                
                // Set flag to indicate we're generating a trip plan (used for guarding restores)
                setIsGeneratingTripPlan(isNewTripRequest);
                
                // Navigate to /trip only when router determines this is a PLAN query
                if (isNewTripRequest) {
                  // Clear the old trip URL stash so we don't show cached old trip
                  try {
                    localStorage.removeItem('tripgen_nav_current_plan');
                  } catch (error) {
                    plannerLogger.error('Error clearing navigation stash:', error);
                  }

                  // Clear old search ID when starting a new trip so we don't load old trip
                  setCurrentSearchId(null);
                  resetInMemoryPlanState(false);
                  await clearCurrentPlan();

                  if (location.pathname !== '/chat' && location.pathname !== '/trip') {
                    navigate('/trip', { replace: true });
                  }

                  startDestinationInfoStream();
                }
              }

              if (data.type === 'intermediate_response') {
                const phase = data.phase || null;
                setLoadingPhase(phase);

                const planMode = data.data?.plan_mode || data.plan_mode;
                // Detect if this is a multi-city partial update (from individual city)
                const isMultiCityPartial =
                  planMode === 'multi_city'
                    ? Boolean(data.city || data.city_key || data.data?.city || data.data?.city_key)
                    : false;
                const hasMultiCityPlan = Boolean(data.data?.multi_city_plan);
                // Determine multi-city context and allow reset when payload is clearly single-city
                let isMultiCityContext = isMultiCityMode || travelData?.plan_mode === 'multi_city' || isMultiCityPartial || hasMultiCityPlan || planMode === 'multi_city';
                if (planMode !== 'multi_city' && !hasMultiCityPlan && !isMultiCityPartial) {
                  isMultiCityContext = false;
                  setIsMultiCityMode(false);
                }
                if (planMode === 'multi_city' || hasMultiCityPlan) {
                  setIsMultiCityMode(true);
                }
                const cityKey = data.city_key || data.data?.city_key;
                const city = data.city || data.data?.city;
                const isCitySpecific = Boolean(cityKey || city);
                const isMergedMultiCityUpdate = hasMultiCityPlan || phase === 'multi_city_skeleton';
                const shouldIgnoreCityUpdate = isMultiCityContext && isCitySpecific && !isMergedMultiCityUpdate;


                if (phase === 'destination_info') {
                  const streamedInfo = data.data?.destination_info;
                  const scope = data.data?.scope;
                  
                  if (streamedInfo) {
                    if (scope === 'country') {
                      // Country-level destination info for multi-city trips
                      setCountryDestinationInfo(streamedInfo);
                      // Clear city-level destination info when country info arrives
                      if (isMultiCityContext || planMode === 'multi_city') {
                        setDestinationInfoRaw(null);
                      }
                    } else if (isMultiCityContext || planMode === 'multi_city') {
                      // In multi-city mode, ignore city-level destination_info updates
                      // Only country-level destination_info should be shown during generation
                      // Individual city destination_info would cause chaotic re-renders
                      // Explicitly do nothing - don't set destinationInfo
                    } else {
                      // City-level destination info for single-city trips
                      setDestinationInfoRaw(streamedInfo);
                    }
                    setDestinationInfoError(null);
                  }
                  setIsDestinationInfoStreaming(false);
                }

                if (phase === 'country_summary') {
                  const countrySummary = data.data?.country_summary;
                  if (countrySummary) {
                    setTravelData((prev) => {
                      if (!prev) return prev;
                      const isMultiCity = prev.plan_mode === 'multi_city' || countrySummary.country;
                      // For multi-city trips, set destination to country name if not already set or if currently a city name
                      let destination = prev.destination;
                      if (isMultiCity && countrySummary.country) {
                        const countryLower = countrySummary.country.toLowerCase();
                        const destLower = (prev.destination || '').toLowerCase();
                        // If destination is not set, or it's a city name (contains comma), set it to country name
                        if (!prev.destination || (destLower.includes(',') && !destLower.includes(countryLower))) {
                          destination = countrySummary.country;
                        }
                      }
                      return { 
                        ...prev, 
                        country_summary: countrySummary,
                        destination,
                        plan_mode: isMultiCity ? 'multi_city' : prev.plan_mode,
                      };
                    });
                  }
                }

                // Process activities_preliminary updates immediately for both single and multi-city trips
                // For multi-city, merge individual city updates incrementally
                if (phase === 'activities_preliminary') {
                  if (!shouldIgnoreCityUpdate) {
                    const preliminaryState = normalizePreliminaryState(data.data);
                    if (preliminaryState) {
                      // Check if destination changed OR if router determined this is a new trip
                      // Use destination check as primary since router decision may not have arrived yet
                      const destinationChanged = travelData?.destination && preliminaryState.destination &&
                        travelData.destination.toLowerCase() !== preliminaryState.destination.toLowerCase();
                      const shouldMerge = !isNewTripRequest && !destinationChanged;
                      hydrateFromTravelData(preliminaryState, { merge: shouldMerge });
                    }
                  }
                }

                if (phase === 'special_activities') {
                  const itemsByDay = data.data?.itemsByDay;
                  if (itemsByDay && typeof itemsByDay === 'object') {
                    setSpecialActivities(itemsByDay);
                  }
                }

                // Process state updates immediately for both single and multi-city trips
                // For multi-city, merge individual city updates incrementally
                const stateUpdate = data.data?.state;
                if (stateUpdate) {
                  if (!shouldIgnoreCityUpdate) {
                    // Check if destination changed OR if router determined this is a new trip
                    // Use destination check as primary since router decision may not have arrived yet
                    const destinationChanged = travelData?.destination && stateUpdate.destination &&
                      travelData.destination.toLowerCase() !== stateUpdate.destination.toLowerCase();
                    const shouldMerge = !isNewTripRequest && !destinationChanged;
                    hydrateFromTravelData(stateUpdate, { merge: shouldMerge });
                  }
                }
              }

              if (data.type === 'final_response' || data.type === 'plan_response') {
                const isCitySpecific = Boolean(data.city || data.city_key || data.data?.city || data.data?.city_key);
                const hasMultiCityPlan = Boolean(data.data?.multi_city_plan);
                const explicitMultiCity = Boolean(
                  hasMultiCityPlan ||
                  data.data?.plan_mode === 'multi_city' ||
                  data.plan_mode === 'multi_city' ||
                  travelData?.plan_mode === 'multi_city'
                );
                const isMultiCityContext = explicitMultiCity || isMultiCityMode;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _isMultiCityFinal = isMultiCityContext && (data.type === 'final_response' || hasMultiCityPlan);
                if (explicitMultiCity) {
                  setIsMultiCityMode(true);
                } else if (!hasMultiCityPlan) {
                  setIsMultiCityMode(false);
                }

                // Skip city-level plan responses in multi-city mode; wait for merged/final payload
                if (data.type === 'plan_response' && isMultiCityContext && isCitySpecific && !hasMultiCityPlan) {
                  continue;
                }

                setLoadingPhase(null);
                const finalDestinationInfo = data.data?.destination_info;
                const responseCountryInfo = data.data?.country_destination_info;


                // CRITICAL: Only check indicators from THE CURRENT RESPONSE, not from stale state
                // isMultiCityContext is okay because it's derived from the current travel context
                const isDefinitelyMultiCity = isMultiCityContext ||
                                             Boolean(responseCountryInfo) ||
                                             Boolean(data.data?.cities?.length > 0);


                // For multi-city trips, ONLY set country info
                // For single-city trips, ONLY set city info
                if (isDefinitelyMultiCity) {
                  if (responseCountryInfo) {
                    setCountryDestinationInfo(responseCountryInfo);
                    setIsMultiCityMode(true);
                  } else {
                    // console.warn('⚠️ [FOLLOW_UP_RESPONSE] Multi-city but no country_destination_info!');
                  }
                  setDestinationInfo(null);
                  if (finalDestinationInfo) {
                    // console.warn('⚠️ [FOLLOW_UP_RESPONSE] Ignoring city-level destination_info for multi-city trip');
                  }
                } else {
                  // Single-city trip: set city info, clear any stale country info
                  if (finalDestinationInfo) {
                    setDestinationInfo(finalDestinationInfo);
                    setDestinationInfoError(null);
                  }
                  setCountryDestinationInfo(null);
                }
                setIsDestinationInfoStreaming(false);
                const stateUpdate = data.data?.state || data.data;
                if (stateUpdate?.destination) {
                  // Include multi-city fields in travel data
                  const planMode = data.data?.plan_mode || (responseCountryInfo ? 'multi_city' : 'single_city');
                  const travelDataUpdate: Partial<TravelData> = {
                    ...stateUpdate,
                    plan_mode: planMode,
                    cities: data.data?.cities,
                    country_summary: data.data?.country_summary || stateUpdate.country_summary,
                    country_destination_info: responseCountryInfo || stateUpdate.country_destination_info || countryDestinationInfo,
                  };
                  // Check if destination changed OR if router determined this is a new trip
                  const destinationChanged = travelData?.destination && stateUpdate.destination &&
                    travelData.destination.toLowerCase() !== stateUpdate.destination.toLowerCase();
                  const shouldMerge = !isNewTripRequest && !destinationChanged;
                  hydrateFromTravelData(travelDataUpdate, { merge: shouldMerge });

                  // Only append completion message once
                  if (!hasCompletedResponse) {
                    const completionText = isNewTripRequest
                      ? 'All set! Your itinerary is ready below.'
                      : 'Plan updated with your changes.';
                    appendMessage({ text: completionText, isUser: false });
                    void persistChatMessage(completionText, 'assistant', undefined, chatId);
                    hasCompletedResponse = true;
                  }
                }
                setLoading(false);
              }

              if (data.type === 'search_saved') {
                const searchId = data.data?.search_id;
                if (searchId) {
                  void handleSearchSaved(searchId);
                }
              }
            } catch (streamError) {
              plannerLogger.error('Failed to parse follow-up chunk', streamError);
            }
          }
        }
      } catch (err: any) {
        plannerLogger.error('sendFollowUp error:', err);
        setError(err?.message || 'Failed to send follow-up.');
      } finally {
        setLoading(false);
        setLoadingPhase(null);
        setIsDestinationInfoStreaming(false);
        setIsGeneratingTripPlan(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendMessage, hydrateFromTravelData, travelData, clearCurrentPlan, navigate, resetPlanView, ensureActiveChat, persistChatMessage, handleSearchSaved, location.pathname, currentSearchId, applySaveResult],
  );

  const respondToConfirmation = React.useCallback(
    async (confirmationId: string, choice: 'yes' | 'no') => {
      let confirmation: ConfirmationPrompt | undefined;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.confirmation?.id === confirmationId) {
            confirmation = msg.confirmation;
            return {
              ...msg,
              confirmation: { ...msg.confirmation, responded: choice },
            };
          }
          return msg;
        }),
      );

      if (!confirmation) {
        return;
      }

      const chatId = await ensureActiveChat();

      if (choice === 'no') {
        const declineText = 'No worries—canceled this request.';
        appendMessage({ text: declineText, isUser: false });
        if (chatId) {
          void persistChatMessage(declineText, 'assistant', undefined, chatId);
        }
        return;
      }

      const acknowledgement =
        confirmation.source === 'initial'
          ? 'Yes, go ahead and create this plan.'
          : 'Yes, please proceed.';
      appendMessage({ text: acknowledgement, isUser: true });
      if (chatId) {
        void persistChatMessage(acknowledgement, 'user', undefined, chatId);
      }

      if (confirmation.source === 'initial' && confirmation.tripParameters) {
        await generatePlan(confirmation.tripParameters, { confirmCreation: true, skipUserMessage: true });
        return;
      }

      if (confirmation.source === 'followup' && confirmation.retryMessage) {
        await sendFollowUp(confirmation.retryMessage, { confirmCreation: true, skipUserMessage: true });
        return;
      }

      const fallback = 'I need the original request to continue. Please try again.';
      appendMessage({ text: fallback, isUser: false });
      if (chatId) {
        void persistChatMessage(fallback, 'assistant', undefined, chatId);
      }
    },
    [appendMessage, ensureActiveChat, generatePlan, persistChatMessage, sendFollowUp],
  );

  const value: PlannerContextValue = React.useMemo(
    () => ({
      travelData,
      isMultiCityMode,
      destinationInfo,
      countryDestinationInfo,
      isDestinationInfoLoading,
      destinationInfoError,
      setDestinationInfo,
      setCountryDestinationInfo,
      summary,
      sections,
      restaurants,
      specialActivities,
      weather,
      isWeatherLoading,
      tips: travelData?.additional_info || [],
      safetyNotes: travelData?.safety_notes || [],
      packingList: travelData?.packing_list || [],
      activeExperience,
      messages,
      loading,
      loadingPhase,
      isGeneratingTripPlan,
      isAnyOperationInProgress,
      setOperationInProgress: setIsAnyOperationInProgress,
      error,
      currentSearchId,
      destinationImageUrls,
      updateDestinationImages,
      chats,
      chatsLoading,
      currentChatId,
      attachedTrips,
      activeChatTripId,
      refreshChats,
      selectChat,
      startNewChat,
      markTripAsActive,
      applySaveResult,
      setShareViewLock,
      hydrateFromTravelData,
      toggleExperienceSelection,
      toggleAll,
      openExperience,
      generatePlan,
      sendFollowUp,
      respondToConfirmation,
      resetConversation,
      resetPlanner,
      clearCurrentPlan,
    }),
    [
      travelData,
      destinationInfo,
      countryDestinationInfo,
      isMultiCityMode,
      destinationInfoError,
      isDestinationInfoLoading,
      setDestinationInfo,
      setCountryDestinationInfo,
      summary,
      sections,
      restaurants,
      specialActivities,
      weather,
      isWeatherLoading,
      activeExperience,
      messages,
      loading,
      loadingPhase,
      isGeneratingTripPlan,
      isAnyOperationInProgress,
      setIsAnyOperationInProgress,
      error,
      currentSearchId,
      destinationImageUrls,
      updateDestinationImages,
      chats,
      chatsLoading,
      currentChatId,
      attachedTrips,
      activeChatTripId,
      refreshChats,
      selectChat,
      startNewChat,
      markTripAsActive,
      applySaveResult,
      setShareViewLock,
      hydrateFromTravelData,
      toggleExperienceSelection,
      toggleAll,
      openExperience,
      generatePlan,
      sendFollowUp,
      respondToConfirmation,
      resetConversation,
      resetPlanner,
      clearCurrentPlan,
    ],
  );

  return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
};

export const usePlanner = (): PlannerContextValue => {
  const context = React.useContext(PlannerContext);
  if (!context) {
    throw new Error('usePlanner must be used within a PlannerProvider');
  }
  return context;
};
const plannerLogger = createLogger('PlannerContext');
